import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import LabResults from './LabResults'
import IntakeSurvey from './IntakeSurvey'
import WeeklyCheckin from './WeeklyCheckin'
import ReintroductionSurvey from './ReintroductionSurvey'
import SlipupSurvey from './SlipupSurvey'
import ComplianceAudit from './ComplianceAudit'
import CheckinHistory from './CheckinHistory'
import FoodMap from './FoodMap'
import { isTestAccount, testSkipDays } from '../utils/testMode'
import ReintroTab from './ReintroTab'
import AskSensify from './AskSensify'
import MaintainHub from './MaintainHub'
import { protocolDay } from '../utils/protocolDay'
import { AWAY_GATE, getProtocolFoods } from '../utils/protocolEngine'
import { todayLocal, localDateString, localDateOffset } from '../utils/dateUtils'
import Support from './Support'
import CommonTrackDecision from './CommonTrackDecision'
import TrackingLanding from './TrackingLanding'
import DailyCheckin from './DailyCheckin'
import BaselineSharpness from '../components/BaselineSharpness'
import SuspectBoard from '../components/SuspectBoard'
import { HouseholdShareCard } from './HouseholdCard'
import {
  generateDay1Message,
  generateDay3Message,
  generateDay14Message,
  generateDay28Message,
  generateDay57Message,
  checkMilestoneShown,
  markMilestoneShown,
  normalizeMilestones,
  serializeMilestones,
} from '../utils/aiInsights'

const css = `
  .snfy-app { min-height: 100vh; color: var(--ink); }

  .snfy-nav {
    background: rgba(255,255,255,0.88);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    box-shadow: 0 1px 0 rgba(56,62,48,0.06);
    padding: 0 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 56px;
    position: sticky;
    top: 0;
    z-index: 100;
  }
  .snfy-logo { font-family: var(--font-display); font-size: 20px; font-weight: 500; color: var(--ink); letter-spacing: -0.3px; }
  .snfy-msg-btn { position: relative; width: 38px; height: 38px; border-radius: var(--r-sm); border: none; background: var(--paper); box-shadow: var(--shadow-1); cursor: pointer; display: flex; align-items: center; justify-content: center; }
  .snfy-msg-btn:hover { background: var(--forest-050); }
  .snfy-msg-icon { font-size: 16px; color: var(--forest-500); }
  .snfy-msg-badge { position: absolute; top: -6px; right: -6px; min-width: 18px; height: 18px; border-radius: 9px; background: var(--forest-500); color: white; font-size: 10px; font-weight: 700; display: flex; align-items: center; justify-content: center; padding: 0 5px; }
  .snfy-logo em { color: var(--forest-500); font-style: italic; }
  .snfy-nav-tabs { display: flex; gap: 2px; }
  .snfy-tab { font-size: 13px; font-weight: 500; color: var(--gray); padding: 6px 12px; border: none; background: none; cursor: pointer; transition: color var(--t-fast) var(--ease); letter-spacing: -0.1px; border-bottom: 2px solid transparent; }
  .snfy-tab.active { color: var(--ink); font-weight: 600; border-bottom: 2px solid var(--forest-500); }
  .snfy-tab:hover:not(.active) { color: var(--ink); }
  .snfy-bottombar { display: none; }
  .snfy-more-sheet { display: none; }
  @media (max-width: 640px) {
    .snfy-nav-tabs { display: none !important; }
    .snfy-app { padding-bottom: 76px; }
    .snfy-bottombar { position: fixed; bottom: 0; left: 0; right: 0; z-index: 80; display: flex; background: rgba(250,248,244,0.97); backdrop-filter: blur(10px); box-shadow: 0 -1px 0 rgba(56,62,48,0.07); padding: 5px 4px calc(7px + env(safe-area-inset-bottom)); }
    .snfy-bb-btn { flex: 1; background: none; border: none; font-size: 11px; color: var(--gray); padding: 7px 2px 5px; cursor: pointer; display: flex; flex-direction: column; align-items: center; gap: 4px; }
    .snfy-bb-dot { width: 4px; height: 4px; border-radius: 50%; background: transparent; }
    .snfy-bb-btn.active { color: var(--ink); font-weight: 600; }
    .snfy-bb-btn.active .snfy-bb-dot { background: var(--forest-500); }
    .snfy-more-sheet { position: fixed; bottom: 62px; right: 10px; z-index: 81; display: block; background: var(--paper); border: none; border-radius: var(--r-md); box-shadow: var(--shadow-2); padding: 6px; min-width: 172px; }
    .snfy-more-item { display: block; width: 100%; text-align: left; background: none; border: none; font-size: 13.5px; color: var(--ink); padding: 11px 13px; border-radius: var(--r-sm); cursor: pointer; }
    .snfy-more-item:active { background: var(--tint-warm); }
  }
  @media (max-width: 600px) {
    .snfy-nav { padding: 0 14px; gap: 6px; }
    .snfy-logo { font-size: 18px; flex-shrink: 0; }
    .snfy-nav-tabs { overflow-x: auto; -webkit-overflow-scrolling: touch; flex: 1; scrollbar-width: none; }
    .snfy-nav-tabs::-webkit-scrollbar { display: none; }
    .snfy-tab { font-size: 12px; padding: 6px 8px; white-space: nowrap; flex-shrink: 0; }
    .snfy-phase-pill { display: none; }
    .snfy-nav-right { gap: 6px; flex-shrink: 0; }
    .snfy-signout { font-size: 11px; }
  }
  .snfy-nav-right { display: flex; align-items: center; gap: 10px; }
  .snfy-phase-pill { font-family: var(--font-mono); font-size: 10px; font-weight: 500; letter-spacing: 0.17em; text-transform: uppercase; color: var(--forest-500); background: var(--forest-100); padding: 5px 12px; border-radius: var(--r-pill); }
  .snfy-signout { font-size: 12px; color: var(--gray); background: none; border: none; cursor: pointer; }
  .snfy-signout:hover { color: var(--ink); }

  /* Hero band: slim, full-width, one sentence + one CTA + one labeled progress line */
  .snfy-heroband { max-width: 1140px; margin: 0 auto; padding: 20px 16px 0; }
  .snfy-hero-top { position: relative; display: flex; align-items: center; gap: 18px; flex-wrap: wrap; }
  .snfy-hero-big { font-family: var(--font-display); font-size: 29px; font-weight: 300; color: #FAF8F4; line-height: 1.12; letter-spacing: -0.3px; }
  .snfy-hero-phase { display: inline-block; vertical-align: middle; margin-left: 10px; font-family: var(--font-mono); font-size: 10px; font-weight: 500; letter-spacing: 0.17em; text-transform: uppercase; color: var(--forest-900); background: var(--forest-300); border-radius: var(--r-pill); padding: 4px 10px; transform: translateY(-4px); }
  @media (min-width: 900px) {
    .snfy-heroband { padding: 24px 28px 0; }
    .snfy-hero-big { font-size: 38px; }
  }
  @media (prefers-reduced-motion: reduce) {
    .snfy-hero-ring { animation: none !important; }
    .snfy-pfill { transition: none; }
  }

  .snfy-layout { padding: 20px 16px 40px; max-width: 1140px; margin: 0 auto; display: flex; flex-direction: column; gap: 14px; }
  .snfy-layout > div { display: contents; }
  .snfy-avoiding { order: 1; }
  .snfy-garden { order: 1; }
  .snfy-guide-slot { order: 2; }
  .snfy-insight { order: 3; }
  .snfy-weekly-card { order: 4; }
  .snfy-comp { order: 5; }
  .snfy-trends { order: 6; }
  .snfy-layout .snfy-action, .snfy-layout .snfy-greeting, .snfy-layout .snfy-phase { order: -1; }
  @media (min-width: 900px) {
    .snfy-layout { display: grid; grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr); gap: 20px; padding: 24px 28px 56px; align-items: start; }
    .snfy-layout > div { display: block; }
  }

  /* Greeting */
  .snfy-greeting { margin-bottom: 16px; }
  .snfy-greeting h1 { font-family: var(--font-display); font-size: 30px; font-weight: 300; line-height: 1.2; margin-bottom: 5px; letter-spacing: -0.5px; }
  .snfy-greeting h1 em { font-style: italic; color: var(--forest-500); }
  .snfy-greeting p { font-size: 13px; color: var(--gray); line-height: 1.55; }

  /* Dark protocol card */
  .snfy-phase {
    position: relative; overflow: hidden; margin-bottom: 14px; padding: 24px; color: #FAF8F4;
    border-radius: 28px;
    background:
      radial-gradient(120% 90% at 85% 0%, rgba(139, 174, 138, 0.32), transparent 55%),
      linear-gradient(160deg, var(--forest-700) 0%, var(--forest-900) 70%);
    box-shadow: var(--shadow-3);
  }
  .snfy-phase-orb { position: absolute; top: -70px; right: -70px; width: 240px; height: 240px; border-radius: 50%; background: #8BAE8A; animation: snfyGlow 5s ease-in-out infinite; pointer-events: none; }
  .snfy-phase-orb2 { position: absolute; bottom: -90px; left: -60px; width: 200px; height: 200px; border-radius: 50%; background: #8BAE8A; opacity: 0.04; pointer-events: none; }
  @keyframes snfyGlow { 0%, 100% { opacity: 0.05; } 50% { opacity: 0.11; } }
  .snfy-phase-eyebrow { font-family: var(--font-mono); font-size: 10px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.17em; color: rgba(250,248,244,0.55); margin-bottom: 10px; }
  .snfy-phase-day { font-family: var(--font-display); font-size: 64px; font-weight: 300; line-height: 0.9; color: white; margin-bottom: 4px; font-variant-numeric: lining-nums; }
  .snfy-phase-of { font-size: 13px; color: rgba(250,248,244,0.45); margin-bottom: 18px; }
  .snfy-pbar { position: relative; background: rgba(250,248,244,0.13); border-radius: 5px; height: 9px; margin-bottom: 9px; }
  .snfy-pfill { position: absolute; top: 0; bottom: 0; left: 0; background: linear-gradient(90deg, var(--forest-150), var(--forest-300)); border-radius: 5px; box-shadow: 0 0 12px rgba(139, 174, 138, 0.75); transition: width 0.6s var(--ease); }
  .snfy-plabel { font-family: var(--font-mono); font-size: 9px; letter-spacing: 0.14em; text-transform: uppercase; color: rgba(250,248,244,0.55); display: flex; justify-content: space-between; }
  .snfy-phase-streak { display: flex; align-items: center; gap: 8px; margin-top: 16px; }
  .snfy-streak-dot { width: 7px; height: 7px; border-radius: 50%; background: #8BAE8A; flex-shrink: 0; }
  .snfy-streak-text { font-size: 12px; color: rgba(250,248,244,0.45); }
  .snfy-streak-num { font-weight: 500; color: #8BAE8A; }

  /* Action cards */
  .snfy-action { background: var(--paper); border: none; border-radius: var(--r-lg); box-shadow: var(--shadow-2); padding: 22px; margin-bottom: 14px; }
  .snfy-action-tag { display: inline-flex; align-items: center; gap: 6px; font-family: var(--font-mono); font-size: 10px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.17em; color: var(--forest-500); background: var(--forest-100); padding: 5px 11px; border-radius: var(--r-pill); margin-bottom: 12px; }
  .snfy-action-tag.amber { color: var(--limit-700); background: var(--limit-100); }
  .snfy-action-tag.red { color: var(--avoid-700); background: var(--avoid-100); }
  .snfy-action-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--forest-500); animation: snfy-pulse 2s infinite; }
  @keyframes snfy-pulse { 0%,100%{opacity:1;}50%{opacity:0.3;} }
  .snfy-action h2 { font-family: var(--font-display); font-size: 21px; font-weight: 300; margin-bottom: 6px; line-height: 1.3; letter-spacing: -0.3px; }
  .snfy-action h2 em { font-style: italic; color: var(--forest-500); }
  .snfy-action h2 em.amber { color: var(--limit-700); }
  .snfy-action h2 em.red { color: var(--avoid-700); }
  .snfy-action p { font-size: 13px; color: var(--gray); line-height: 1.65; margin-bottom: 14px; }
  .snfy-btn { background: var(--forest-500); color: white; border: none; border-radius: var(--r-md); padding: 12px 20px; font-size: 13px; font-weight: 700; cursor: pointer; box-shadow: var(--shadow-pill); transition: transform var(--t-fast) var(--ease), opacity var(--t-fast) var(--ease); }
  .snfy-btn:hover { transform: translateY(-1px); }
  .snfy-btn:active { transform: scale(0.985); }
  .snfy-btn.amber { background: var(--limit); box-shadow: 0 10px 24px -6px rgba(154, 95, 26, 0.4); }
  .snfy-btn.red { background: var(--avoid); box-shadow: 0 10px 24px -6px rgba(139, 46, 46, 0.4); }

  /* Compliance: compact glanceable strip */
  .snfy-comp { background: var(--paper); border: none; border-radius: var(--r-lg); box-shadow: var(--shadow-2); padding: 22px; margin-bottom: 0; }
  .snfy-comp-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
  .snfy-comp-streak-wrap { display: flex; align-items: baseline; gap: 6px; }
  .snfy-comp-streak-num { font-family: var(--font-display); font-size: 26px; font-weight: 300; line-height: 1; color: var(--forest-500); font-variant-numeric: lining-nums; }
  .snfy-comp-dots { display: flex; gap: 5px; }
  .snfy-dot { flex: 1; height: 34px; border-radius: var(--r-sm); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px; }
  .snfy-dot.yes { background: #4A8C6A; }
  .snfy-dot.no { background: var(--avoid); }
  .snfy-dot.empty { background: var(--tint-warm); }
  .snfy-dot.future { background: transparent; border: 1px dashed rgba(0,0,0,0.12); }
  .snfy-dot.missed { background: #DDDAD1; }
  .snfy-dot.away { background: transparent; border: 1.5px solid #8BAE8A; }
  .snfy-dot.away .snfy-dot-day { color: #3D5C3C; }
  .snfy-dot.away .snfy-dot-mark { color: #8BAE8A; }
  .snfy-dot.today { box-shadow: 0 0 0 2px var(--forest-500); }
  .snfy-dot-day { font-size: 8px; font-weight: 700; letter-spacing: 0.3px; }
  .snfy-dot.yes .snfy-dot-day, .snfy-dot.no .snfy-dot-day { color: rgba(255,255,255,0.85); }
  .snfy-dot.empty .snfy-dot-day, .snfy-dot.future .snfy-dot-day { color: #A8A69E; }
  .snfy-dot.missed .snfy-dot-day { color: #6A6A62; }
  .snfy-dot-mark { font-size: 11px; font-weight: 700; line-height: 1; }
  .snfy-dot.yes .snfy-dot-mark { color: white; }
  .snfy-dot.no .snfy-dot-mark { color: white; }
  .snfy-dot.empty .snfy-dot-mark { color: rgba(0,0,0,0.15); }
  .snfy-dot.future .snfy-dot-mark { color: rgba(0,0,0,0.12); }
  .snfy-dot.missed .snfy-dot-mark { color: #6A6A62; font-weight: 700; }

  /* Pending cards */
  .snfy-pending { background: var(--limit-100); border: none; border-radius: var(--r-md); box-shadow: var(--shadow-1); padding: 14px; margin-bottom: 14px; display: flex; align-items: center; gap: 11px; }
  .snfy-pending-icon { width: 34px; height: 34px; background: var(--limit); border-radius: var(--r-sm); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .snfy-audit-pending { background: var(--tint-warm); border: none; border-radius: var(--r-md); box-shadow: var(--shadow-1); padding: 14px; margin-bottom: 14px; display: flex; align-items: center; gap: 11px; }
  .snfy-audit-icon { width: 34px; height: 34px; background: var(--forest-100); border-radius: var(--r-sm); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }

  /* Avoiding list — full categorized */
  .snfy-avoiding { background: var(--paper); border: none; border-radius: var(--r-lg); box-shadow: var(--shadow-2); padding: 22px; margin-bottom: 0; }
  .snfy-avoid-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
  .snfy-avoid-bar { display: flex; gap: 2px; height: 9px; border-radius: 5px; overflow: hidden; margin-bottom: 20px; }
  .snfy-avoid-bar-seg { height: 9px; transition: flex 0.4s var(--ease); }
  .snfy-avoid-group { margin-bottom: 18px; }
  .snfy-avoid-group:last-child { margin-bottom: 0; }
  .snfy-avoid-group-head { display: flex; align-items: center; gap: 8px; margin-bottom: 11px; }
  .snfy-avoid-level-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
  .snfy-avoid-chips { display: flex; flex-wrap: wrap; gap: 7px; }
  .snfy-avoid-chip { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; padding: 7px 13px; border-radius: var(--r-sm); font-weight: 500; }

  /* Weekly insight — instrument style */
  .snfy-insight { background: linear-gradient(135deg, rgba(139,174,138,0.08), rgba(139,174,138,0.03)), var(--paper); border: none; border-radius: var(--r-lg); box-shadow: var(--shadow-2); padding: 22px; margin-top: 16px; }
  .snfy-weekly-card { margin-top: 14px; }
  .snfy-trends { margin-top: 14px; }
  .snfy-insight-head { display: flex; align-items: center; gap: 7px; margin-bottom: 10px; }
  .snfy-insight-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--forest-300); animation: snfyPulse 1.6s infinite; flex-shrink: 0; }
  .snfy-insight-num { font-family: var(--font-display); font-size: 38px; font-weight: 300; line-height: 1; letter-spacing: -0.02em; color: var(--ink); margin-bottom: 10px; font-variant-numeric: lining-nums; }
  .snfy-insight p { font-size: 13px; line-height: 1.7; color: var(--ink-soft); }
  @keyframes snfyPulse { 0%, 100% { opacity: 0.35; } 50% { opacity: 1; } }

  .snfy-loading { display: flex; align-items: center; justify-content: center; min-height: 60vh; font-size: 14px; color: var(--gray); }
`
// Confetti overlay — brand colors, 3.5 seconds, fades out
function ConfettiOverlay() {
  const canvasRef = React.useRef(null)
  const pieces = React.useRef([])
  const raf = React.useRef(null)

  React.useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    canvas.width = window.innerWidth
    canvas.height = window.innerHeight

    const colors = ['#3D5C3C', '#8BAE8A', '#D4894A', '#FAF8F4', '#EDF3ED', '#4A8C6A', '#C8D8C8']
    for (let i = 0; i < 120; i++) {
      pieces.current.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height - canvas.height,
        w: Math.random() * 10 + 5,
        h: Math.random() * 5 + 3,
        color: colors[Math.floor(Math.random() * colors.length)],
        rotation: Math.random() * 360,
        rotSpeed: (Math.random() - 0.5) * 4,
        vx: (Math.random() - 0.5) * 2,
        vy: Math.random() * 3 + 2,
        opacity: 1,
      })
    }

    let start = null
    const duration = 3500

    const animate = (ts) => {
      if (!start) start = ts
      const elapsed = ts - start
      const progress = Math.min(elapsed / duration, 1)

      ctx.clearRect(0, 0, canvas.width, canvas.height)

      pieces.current.forEach(p => {
        p.x += p.vx
        p.y += p.vy
        p.rotation += p.rotSpeed
        p.opacity = progress > 0.6 ? 1 - ((progress - 0.6) / 0.4) : 1

        ctx.save()
        ctx.globalAlpha = p.opacity
        ctx.translate(p.x + p.w / 2, p.y + p.h / 2)
        ctx.rotate((p.rotation * Math.PI) / 180)
        ctx.fillStyle = p.color
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h)
        ctx.restore()
      })

      if (progress < 1) raf.current = requestAnimationFrame(animate)
    }

    raf.current = requestAnimationFrame(animate)
    return () => { if (raf.current) cancelAnimationFrame(raf.current) }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 999 }}
    />
  )
}

function SymptomGraph({ profile, checkins, activeMetric, setActiveMetric }) {
  const [trendLens, setTrendLens] = React.useState('average')
  const [phaseLens, setPhaseLens] = React.useState('all')
  const metrics = []
  // One metric draws at a time, so the line is always forest: color carries no
  // metric identity, and verdict-family hues stay reserved for verdicts.
  if (profile?.baseline_bloating) metrics.push({ id: 'bloating', label: 'Bloating', baseline: profile.baseline_bloating, color: '#3D5C3C', lowerIsBetter: true })
  if (profile?.baseline_energy) metrics.push({ id: 'energy', label: 'Energy', baseline: profile.baseline_energy, color: '#3D5C3C', lowerIsBetter: false })
  if (profile?.baseline_clarity) metrics.push({ id: 'clarity', label: 'Clarity', baseline: profile.baseline_clarity, color: '#3D5C3C', lowerIsBetter: false })
  if (profile?.baseline_sleep) metrics.push({ id: 'sleep', label: 'Sleep', baseline: profile.baseline_sleep, color: '#3D5C3C', lowerIsBetter: false })
  if (profile?.baseline_gas) metrics.push({ id: 'gas', label: 'Gas', baseline: profile.baseline_gas, color: '#3D5C3C', lowerIsBetter: true })

  const selected = activeMetric || metrics[0]?.id
  const metric = metrics.find(m => m.id === selected) || metrics[0]

  if (!metric || checkins.length === 0) {
    return (
      <div className="sn-card" style={{ marginTop: '14px' }}>
        <div className="sn-micro" style={{ marginBottom: '10px' }}>Symptom trends</div>
        <div style={{ fontSize: '13px', color: 'var(--gray)', lineHeight: 1.65 }}>
          {metrics.length === 0 ? 'Complete your intake survey to set baselines.' : 'Your symptom trends will appear here after your first weekly check-in.'}
        </div>
      </div>
    )
  }

  const sortedCheckins = [...checkins].sort((a, b) => new Date(a.submitted_at) - new Date(b.submitted_at))
  let points = [{ week: 0, val: metric.baseline, label: 'Baseline' }]
  sortedCheckins.forEach((c, i) => {
    if (c.answers?.[metric.id] !== undefined) {
      points.push({ week: c.week_number || i + 1, val: c.answers[metric.id], label: `Week ${c.week_number || i + 1}`, reintro: c.answers?._phase === 'reintroduction' || (c.week_number || i + 1) > 8, exposure: c.answers?._cycle?.phase === 'exposure', cycleFood: c.answers?._cycle?.food || null })
    }
  })

  const allPoints = points
  const allCheckinCount = allPoints.length - 1
  if (phaseLens === 'elimination') points = points.filter(p => !p.reintro)
  else if (phaseLens === 'reintroduction') points = points.filter(p => p.reintro || p.week === 0)

  const latest = points[points.length - 1]
  // Values from actual check-ins (exclude the baseline point at index 0)
  const checkinVals = points.slice(1).map(p => p.val)
  const avgVal = checkinVals.length ? checkinVals.reduce((a, b) => a + b, 0) / checkinVals.length : (latest?.val ?? metric.baseline)
  const usedVal = trendLens === 'average' ? avgVal : (latest?.val ?? metric.baseline)
  const change = metric.baseline
    ? Math.round(((usedVal - metric.baseline) / metric.baseline) * 100)
    : 0
  const improved = metric.lowerIsBetter ? change < 0 : change > 0
  const changeLabel = change === 0 ? 'No change'
    : `${change > 0 ? '+' : ''}${change}% ${trendLens === 'average' ? 'avg' : 'latest'}`

  // SVG graph
  const W = 240, H = 90
  const padL = 20, padR = 10, padT = 10, padB = 20
  const gW = W - padL - padR
  const gH = H - padT - padB
  const maxVal = 10, minVal = 0

  const toX = (i) => padL + (i / Math.max(points.length - 1, 1)) * gW
  const toY = (v) => padT + gH - ((v - minVal) / (maxVal - minVal)) * gH

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${toX(i)} ${toY(p.val)}`).join(' ')
  // Reintro divider: where the story changes chapters (only meaningful in 'all' view)
  const firstReintroIdx = phaseLens === 'all' ? points.findIndex(p => p.reintro) : -1
  const areaD = points.length > 0
    ? `${pathD} L ${toX(points.length - 1)} ${H - padB} L ${toX(0)} ${H - padB} Z`
    : ''

  return (
    <div className="sn-card" style={{ marginTop: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div className="sn-micro">Symptom trends</div>
        <div style={{ fontSize: '11px', fontWeight: 500, color: improved ? '#4A8C6A' : change === 0 ? '#7A7A72' : '#C95B5B' }}>{changeLabel} vs baseline</div>
      </div>
      {allCheckinCount >= 2 && (<>
        <div style={{ display: 'flex', gap: '4px', background: '#F0EEE7', borderRadius: '8px', padding: '3px', marginBottom: '6px' }}>
          <button onClick={() => setTrendLens('week')} style={{ flex: 1, textAlign: 'center', fontSize: '11.5px', fontWeight: trendLens === 'week' ? 600 : 400, color: trendLens === 'week' ? '#1C1C1C' : '#8A8A82', background: trendLens === 'week' ? '#fff' : 'transparent', padding: '5px 0', borderRadius: '6px', border: 'none', cursor: 'pointer', boxShadow: trendLens === 'week' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none' }}>Latest week</button>
          <button onClick={() => setTrendLens('average')} style={{ flex: 1, textAlign: 'center', fontSize: '11.5px', fontWeight: trendLens === 'average' ? 600 : 400, color: trendLens === 'average' ? '#1C1C1C' : '#8A8A82', background: trendLens === 'average' ? '#fff' : 'transparent', padding: '5px 0', borderRadius: '6px', border: 'none', cursor: 'pointer', boxShadow: trendLens === 'average' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none' }}>Overall average</button>
        </div>
        <div style={{ display: 'flex', gap: 4, marginTop: 6, marginBottom: 4 }}>
          {[['all', 'Both phases'], ['elimination', 'Elimination'], ['reintroduction', 'Reintro']].map(([key, label]) => (
            <button key={key} onClick={() => setPhaseLens(key)} style={{ flex: 1, textAlign: 'center', fontSize: '10.5px', fontWeight: phaseLens === key ? 600 : 400, color: phaseLens === key ? '#1C1C1C' : '#8A8A82', background: phaseLens === key ? '#fff' : 'transparent', padding: '4px 0', borderRadius: '6px', border: 'none', boxShadow: phaseLens === key ? '0 1px 2px rgba(0,0,0,0.08)' : 'none', cursor: 'pointer' }}>{label}</button>
          ))}
        </div>
      </>)}

      {/* Metric selector pills */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
        {metrics.map(m => (
          <button
            key={m.id}
            onClick={() => setActiveMetric(m.id)}
            className={selected === m.id ? 'sn-chip sn-chip-on' : 'sn-chip'}
            style={{ padding: '5px 11px', fontSize: '11px' }}
          >{m.label}</button>
        ))}
      </div>

      {/* SVG graph */}
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible' }}>
        {/* Grid lines */}
        {[2, 4, 6, 8, 10].map(v => (
          <line key={v} x1={padL} y1={toY(v)} x2={W - padR} y2={toY(v)} stroke="rgba(0,0,0,0.05)" strokeWidth="1" />
        ))}
        {/* Baseline reference line */}
        <line x1={padL} y1={toY(metric.baseline)} x2={W - padR} y2={toY(metric.baseline)} stroke={metric.color} strokeWidth="1" strokeDasharray="3,3" opacity="0.4" />

        {/* Area fill */}
        {areaD && <path d={areaD} fill={metric.color} opacity="0.07" />}
        {/* Line */}
        <path d={pathD} fill="none" stroke={metric.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {firstReintroIdx > 0 && (<>
          <line x1={toX(firstReintroIdx)} y1={padT} x2={toX(firstReintroIdx)} y2={H - padB} stroke="#73736A" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
          <text x={toX(firstReintroIdx) + 3} y={padT + 7} fontSize="6" fill="#73736A" fontFamily="DM Mono, monospace" letterSpacing="0.5">REINTRO</text>
        </>)}

        {/* Points */}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={toX(i)} cy={toY(p.val)} r="4" fill="white" stroke={p.exposure ? '#73736A' : metric.color} strokeWidth="2">{p.cycleFood && <title>{`${p.cycleFood} ${p.exposure ? 'exposure' : 'washout'} week`}</title>}</circle>
            {p.exposure && <circle cx={toX(i)} cy={toY(p.val)} r="7" fill="none" stroke="#73736A" strokeWidth="1" opacity="0.5" />}
            {i === points.length - 1 && (
              <text x={toX(i)} y={toY(p.val) - 8} textAnchor="middle" fontSize="10" fill={metric.color} fontFamily="Plus Jakarta Sans, sans-serif" fontWeight="500">{p.val}</text>
            )}
          </g>
        ))}

        {/* X axis labels */}
        {points.map((p, i) => (
          <text key={i} x={toX(i)} y={H - 4} textAnchor="middle" fontSize="8" fill="#7A7A72" fontFamily="Plus Jakarta Sans, sans-serif">{p.label}</text>
        ))}
      </svg>

      {/* Summary row */}
      <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
        <div style={{ flex: 1, background: 'var(--tint-warm)', borderRadius: 'var(--r-md)', padding: '11px 13px' }}>
          <div className="sn-micro sn-micro-dim" style={{ marginBottom: '4px' }}>Baseline</div>
          <div className="sn-num" style={{ fontSize: '22px' }}>{metric.baseline}</div>
        </div>
        <div style={{ flex: 1, background: 'var(--tint-warm)', borderRadius: 'var(--r-md)', padding: '11px 13px' }}>
          <div className="sn-micro sn-micro-dim" style={{ marginBottom: '4px' }}>Latest</div>
          <div className="sn-num" style={{ fontSize: '22px', color: improved ? '#4A8C6A' : change === 0 ? 'var(--ink)' : '#C95B5B' }}>{latest?.val ?? '—'}</div>
        </div>
        <div style={{ flex: 1, background: 'var(--tint-warm)', borderRadius: 'var(--r-md)', padding: '11px 13px' }}>
          <div className="sn-micro sn-micro-dim" style={{ marginBottom: '4px' }}>Check-ins</div>
          <div className="sn-num" style={{ fontSize: '22px' }}>{points.length - 1}</div>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// WeekFactorCards — expandable summary cards for daily factors.
// Collapsed: icon + name + "norm X · this week Y". Tap → accordion
// opens: Your norm vs This week, then a 7-day bar chart (M–S).
// Bar height = the band they reported. Missed days show a dash.
// Purely descriptive (their own logs mirrored back); causal
// interpretation stays gated behind Layer 2 / physician sign-off.
// ============================================================
const FACTOR_DEFS = {
  sleep: {
    name: 'Sleep',
    numeric: true, unit: 'hrs', scaleMax: 10,
    bands: ['under6', '6-7', '7-8', '8plus'],
    labels: { under6: 'Under 6 hrs', '6-7': '6\u20137 hrs', '7-8': '7\u20138 hrs', '8plus': '8+ hrs' },
    colors: ['#C9D8C4', '#9FBE9A', '#6E9A6B', '#3D5C3C'],
    normField: 'baseline_avg_sleep',
    icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3D5C3C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>,
  },
  stress: {
    name: 'Stress',
    bands: ['low', 'mild', 'moderate', 'high', 'severe'],
    labels: { low: 'Low', mild: 'Mild', moderate: 'Moderate', high: 'High', severe: 'Severe' },
    colors: ['#DCE7D8', '#E4DFCB', '#EED9A8', '#E8B36B', '#D98A4A'],
    normField: 'baseline_avg_stress',
    icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3D5C3C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  },
  hydration: {
    name: 'Water',
    numeric: true, unit: 'cups', scaleMax: 12,
    bands: ['under3', '3-5', '6-8', '8plus'],
    labels: { under3: 'Under 3 glasses', '3-5': '3\u20135 glasses', '6-8': '6\u20138 glasses', '8plus': '8+ glasses' },
    colors: ['#C9D8C4', '#9FBE9A', '#6E9A6B', '#3D5C3C'],
    normField: 'baseline_avg_hydration',
    icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3D5C3C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>,
  },
  drinks: {
    name: 'Alcohol',
    numeric: true, unit: '/ week',
    bands: [],
    labels: { '1-3': '1\u20133 / week', '4-7': '4\u20137 / week', '8-14': '8\u201314 / week', '15plus': '15+ / week' },
    colors: [],
    normField: 'baseline_avg_drinks_week',
    icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3D5C3C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 22h8"/><path d="M12 11v11"/><path d="m19 3-7 8-7-8Z"/></svg>,
  },
}

function WeekFactorCards({ days, factors, profile }) {
  const [open, setOpen] = useState(null)
  if (!factors || factors.length === 0) return null
  const byDate = {}
  factors.forEach(f => { byDate[f.log_date] = f })

  const factorKeys = profile?.drinks_alcohol === true ? ['sleep', 'stress', 'hydration', 'drinks'] : ['sleep', 'stress', 'hydration']

  const numAvg = (vals) => vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null
  const threeVals = (key) => {
    const def = FACTOR_DEFS[key]
    const intake = parseFloat(profile?.[def.normField])
    const weekVals = days.map(d => parseFloat(byDate[d.dateStr]?.[key])).filter(v => !isNaN(v))
    const protoVals = (factors || []).map(f => parseFloat(f[key])).filter(v => !isNaN(v))
    if (key === 'drinks') {
      // Weeks ELAPSED since protocol start, not logged-days/7 (QA 2026-11-03 #5):
      // a sparse logger's rate was inflated by counting only the days they logged.
      const weeks = (() => {
        const start = profile?.protocol_start_date
        if (start) {
          const [y, m, d] = String(start).split('T')[0].split('-').map(Number)
          const elapsed = Math.floor((new Date() - new Date(y, m - 1, d)) / (7 * 86400000))
          if (elapsed >= 1) return elapsed
        }
        return Math.max(1, Math.ceil((factors || []).length / 7))
      })()
      return {
        intake: isNaN(intake) ? null : intake,
        week: weekVals.length ? weekVals.reduce((a, b) => a + b, 0) : null,
        proto: protoVals.length ? protoVals.reduce((a, b) => a + b, 0) / weeks : null,
        weekN: weekVals.length, protoN: (factors || []).length,
      }
    }
    return {
      intake: isNaN(intake) ? null : intake,
      week: numAvg(weekVals), proto: numAvg(protoVals),
      weekN: weekVals.length, protoN: protoVals.length,
    }
  }
  const fmt1 = (v) => v == null ? '\u2014' : String(Math.round(v * 10) / 10)

  const weekAvgLabel = (key) => {
    const def = FACTOR_DEFS[key]
    if (key === 'drinks') {
      const vals = days.map(d => byDate[d.dateStr]?.drinks).filter(v => v != null)
      if (vals.length === 0) return null
      const total = vals.reduce((a, b) => a + b, 0)
      return `${total} total`
    }
    if (def.numeric) {
      const vals = days.map(d => parseFloat(byDate[d.dateStr]?.[key])).filter(v => !isNaN(v))
      if (vals.length === 0) return null
      const avg = Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10
      return `${String(avg)} ${def.unit}`
    }
    const idxs = days.map(d => byDate[d.dateStr]?.[key]).filter(v => v != null).map(v => (def.bands || []).indexOf(v)).filter(i => i >= 0)
    if (idxs.length === 0) return null
    const avg = Math.round(idxs.reduce((a, b) => a + b, 0) / idxs.length)
    return def.labels[def.bands[avg]]
  }

  // Compact stat strip: one glanceable row of the week's factors; tapping a
  // cell expands its full detail below (expand-on-tap preserved).
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', gap: 7 }}>
        {factorKeys.map(key => {
          const def = FACTOR_DEFS[key]
          const weekAvg = weekAvgLabel(key)
          if (!weekAvg) return null
          const isOpen = open === key
          return (
            <button key={key} type="button" onClick={() => setOpen(isOpen ? null : key)} style={{
              flex: 1, minWidth: 0, border: 'none', cursor: 'pointer',
              background: isOpen ? 'var(--forest-100)' : 'var(--tint-warm)', borderRadius: 10,
              padding: '9px 4px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
            }}>
              <span className="sn-micro sn-micro-dim">{def.name}</span>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: isOpen ? 'var(--forest-500)' : 'var(--ink-soft)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>{weekAvg.toLowerCase()}</span>
            </button>
          )
        })}
      </div>
      {open && (() => {
        const key = open
        const def = FACTOR_DEFS[key]
        const rawNorm = profile?.[def.normField]
        const norm = def.numeric
          ? (rawNorm != null && !isNaN(parseFloat(rawNorm)) ? `${parseFloat(rawNorm)} ${def.unit}` : null)
          : ((def.labels || {})[rawNorm] || null)
        const weekAvg = weekAvgLabel(key)
        if (!weekAvg) return null
        return (
          <div style={{ background: 'var(--tint-warm)', borderRadius: 'var(--r-md)', padding: '12px 14px', marginTop: 7 }}>
              <>
                {def.numeric ? (() => {
                  const tv = threeVals(key)
                  const present = [tv.intake, tv.week, tv.proto].filter(v => v != null)
                  const max = Math.max(...present, 1)
                  const bars = [
                    { label: 'Intake', sub: 'your norm', v: tv.intake, color: '#E0DED6', tcolor: '#7A7A72' },
                    { label: 'This week', sub: `${tv.weekN} check-in${tv.weekN !== 1 ? 's' : ''}`, v: tv.week, color: '#8BAE8A', tcolor: '#3D5C3C' },
                    { label: 'Protocol', sub: `${tv.protoN} day${tv.protoN !== 1 ? 's' : ''}`, v: tv.proto, color: '#3D5C3C', tcolor: '#3D5C3C' },
                  ]
                  return (
                    <div style={{ marginTop: 11 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, height: 84, padding: '0 6px' }}>
                        {bars.map(b => (
                          <div key={b.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%', justifyContent: 'flex-end' }}>
                            <div style={{ fontFamily: 'Fraunces, serif', fontSize: 14, color: b.v == null ? '#C8C6BE' : b.tcolor }}>{fmt1(b.v)}</div>
                            <div style={{ width: '100%', maxWidth: 46, height: b.v == null ? 3 : `${Math.max((b.v / max) * 72, 6)}%`, background: b.v == null ? '#EFEDE6' : b.color, borderRadius: '7px 7px 3px 3px' }} />
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: 14, padding: '0 6px', marginTop: 4 }}>
                        {bars.map(b => (
                          <div key={b.label} style={{ flex: 1, textAlign: 'center', fontSize: 8.5, color: '#8A8A82', lineHeight: 1.4 }}>{b.label}<br /><span style={{ color: '#B8B6AE' }}>{b.sub}</span></div>
                        ))}
                      </div>
                    </div>
                  )
                })() : (
                <div style={{ display: 'flex', gap: 20, marginTop: 9 }}>
                  {norm && (
                    <div>
                      <div className="sn-micro sn-micro-dim">Your norm</div>
                      <div style={{ fontSize: 13.5, fontWeight: 500, color: '#8A8A82', marginTop: 1 }}>{norm}</div>
                    </div>
                  )}
                  <div>
                    <div className="sn-micro sn-micro-dim">This week</div>
                    <div style={{ fontSize: 13.5, fontWeight: 500, color: '#3D5C3C', marginTop: 1 }}>{weekAvg}{key === 'drinks' ? '' : ' avg'}</div>
                  </div>
                </div>
                )}
                <div style={{ marginTop: 11, paddingTop: 9, borderTop: '1px solid rgba(0,0,0,0.05)' }} onClick={e => e.stopPropagation()}>
                  <div className="sn-micro sn-micro-dim" style={{ marginBottom: 7 }}>This week, day by day</div>
                  <div style={{ display: 'flex', gap: 7, alignItems: 'flex-end', height: 34 }}>
                    {days.map((d, i) => {
                      const val = byDate[d.dateStr]?.[key]
                      if (key === 'drinks') {
                        if (val == null) {
                          if (d.isFuture) return <div key={i} style={{ flex: 1 }} />
                          return (
                            <div key={i} style={{ flex: 1, display: 'flex', justifyContent: 'center' }} title={`${d.day} \u00b7 not logged`}>
                              <div style={{ width: 9, height: 2.5, borderRadius: 2, background: '#C9C6BC' }} />
                            </div>
                          )
                        }
                        const pct = val === 0 ? 12 : Math.min(100, Math.round((val / 5) * 100))
                        const color = val === 0 ? '#DCE7D8' : val <= 2 ? '#EED9A8' : val <= 4 ? '#E8B36B' : '#D98A4A'
                        return <div key={i} title={`${d.day} \u00b7 ${val} ${val === 1 ? 'drink' : 'drinks'}`} style={{ flex: 1, height: `${pct}%`, background: color, borderRadius: 4 }} />
                      }
                      if (def.numeric) {
                        const n = parseFloat(val)
                        if (!isNaN(n)) {
                          const pct = Math.max(10, Math.min(100, Math.round((n / (def.scaleMax || 10)) * 100)))
                          return <div key={i} title={`${d.day} · ${n} ${def.unit}`} style={{ flex: 1, height: `${pct}%`, background: '#6E9A6B', borderRadius: 4 }} />
                        }
                        if (d.isFuture) return <div key={i} style={{ flex: 1 }} />
                        return (
                          <div key={i} style={{ flex: 1, display: 'flex', justifyContent: 'center' }} title={`${d.day} · not logged`}>
                            <div style={{ width: 9, height: 2.5, borderRadius: 2, background: '#C9C6BC' }} />
                          </div>
                        )
                      }
                      const idx = val != null && def.bands ? def.bands.indexOf(val) : -1
                      if (idx >= 0) {
                        const pct = Math.round(((idx + 1) / def.bands.length) * 100)
                        return <div key={i} title={`${d.day} \u00b7 ${def.labels[val]}`} style={{ flex: 1, height: `${pct}%`, background: def.colors[idx], borderRadius: 4 }} />
                      }
                      if (d.isFuture) return <div key={i} style={{ flex: 1 }} />
                      return (
                        <div key={i} style={{ flex: 1, display: 'flex', justifyContent: 'center' }} title={`${d.day} \u00b7 not logged`}>
                          <div style={{ width: 9, height: 2.5, borderRadius: 2, background: '#C9C6BC' }} />
                        </div>
                      )
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: 7, marginTop: 4 }}>
                    {days.map((d, i) => (
                      <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 9.5, color: '#A8A69E' }}>{d.day}</div>
                    ))}
                  </div>
                </div>
              </>
          </div>
        )
      })()}
    </div>
  )
}

export default function Dashboard({ session, onLogout, isAdmin, onAdmin }) {
  const [screen, setScreen] = useState('dashboard')
  const [tab, setTab] = useState('home')
  const [moreOpen, setMoreOpen] = useState(false)
  const [profile, setProfile] = useState(null)
  const [labResult, setLabResult] = useState(null)
  const [reintroRows, setReintroRows] = useState([])  // live reintro cycles for the projection

  useEffect(() => {
    if (!session?.user?.id) return
    // Reintro cycles feed the live completion projection: completed rows
    // leave the queue, the active row's started_at carries restarts.
    supabase.from('reintroduction_results')
      .select('food, sensitivity_level, verdict, started_at')
      .eq('user_id', session.user.id)
      .then(({ data }) => setReintroRows(data || []))
  }, [session?.user?.id])

  const [weeklyDue, setWeeklyDue] = useState(false)
  const [checkinLate, setCheckinLate] = useState(false)
  const [activeCheckinWeek, setActiveCheckinWeek] = useState(1)
  const [activeReintroId, setActiveReintroId] = useState(null)
  const [activeCycleLite, setActiveCycleLite] = useState(null)
  const [awayGap, setAwayGap] = useState(null)
  const [recordStats, setRecordStats] = useState({ days: 0, events: 0 })  // { days, hard } when the away-gate should show
  const [complianceData, setComplianceData] = useState([])
  const [awayDates, setAwayDates] = useState(new Set())
  const [weekFactors, setWeekFactors] = useState([])
  const [consecutiveNOs, setConsecutiveNOs] = useState(0)
  const [pendingAudit, setPendingAudit] = useState(false)
  const [auditGuidance, setAuditGuidance] = useState(null)
  const [dailyDone, setDailyDone] = useState(false)
  const [unreadMsgs, setUnreadMsgs] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [milestoneMessage, setMilestoneMessage] = useState(null)
  const [milestoneKey, setMilestoneKey] = useState(null)
  const [legendOpen, setLegendOpen] = useState(false)
  const [guideOpen, setGuideOpen] = useState(false)
  const [showConfetti, setShowConfetti] = useState(false)
  const [checkins, setCheckins] = useState([])
  const [activeGraphMetric, setActiveGraphMetric] = useState(null)

  const name = session?.user?.user_metadata?.full_name?.split(' ')[0] || 'there'

  const getGreeting = () => {
    const h = new Date().getHours()
    // Midnight to 4am is still the night before: never "Good morning" at 12:30am.
    if (h < 4) return 'Good evening'
    if (h < 12) return 'Good morning'
    if (h < 17) return 'Good afternoon'
    return 'Good evening'
  }

  useEffect(() => { loadData() }, [screen])

  // Pick up server-side changes (lab approval, track assignment) when the
  // user returns to the tab — there is no realtime subscription, so without
  // this the "under review" card survives an approval until a full reload
  // (QA 2026-10-22 #3).
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') loadData() }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  const loadData = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      // A failed profile fetch must NOT fall through to the new-user setup
      // screen (QA 2026-11-03 #1): every state card keys off MISSING data, so a
      // network blip showed a mid-protocol customer "let's get you set up" and
      // risked an intake overwrite. Treat a fetch error as an error, not empty.
      const { data: p, error: pErr } = await supabase.from('profiles').select('*').eq('id', session.user.id).single()
      if (pErr) { setLoadError(true); setLoading(false); return }
      setProfile(p)

      const { data: l } = await supabase.from('lab_results').select('*').eq('user_id', session.user.id).order('submitted_at', { ascending: false }).limit(1).maybeSingle()

      // Protocol track seam: the resolver decides which foods drive the protocol.
      // - flagged track  -> the real lab foods, untouched (zero change to today's flow)
      // - common track   -> the chosen tier's foods, injected as labResult.foods so
      //                     every downstream consumer (ReintroTab, compliance, checkins)
      //                     works unchanged without reading the track directly.
      // - declined       -> no protocol foods.
      // Only remap once the user has actively chosen (track_decision === 'active');
      // before that they're in the decision flow and shouldn't have a running protocol.
      let resolvedLab = l
      if (p?.protocol_track === 'common' && p?.track_decision === 'active') {
        const resolved = getProtocolFoods(p, l)
        resolvedLab = { ...(l || {}), foods: resolved.foods, status: 'approved' }
      }
      setLabResult(resolvedLab)

      // Away-gate: consecutive silent days mid-elimination (finding #7).
      try {
        if (p?.protocol_start_date && p?.track_decision !== 'declined') {
          const phaseNow = protocolDay(p.protocol_start_date) <= 56 ? 'elimination' : 'reintroduction'
          if (phaseNow === 'elimination' && protocolDay(p.protocol_start_date) > 1) {
            const { data: lastLog } = await supabase.from('daily_factors').select('log_date').eq('user_id', session.user.id).order('log_date', { ascending: false }).limit(1).maybeSingle()
            // A resolved gap stays resolved: the resume attestation is an anchor too, so the
            // modal never re-asks about days the user already vouched for. A NEW 4+ day gap
            // after the attestation re-triggers naturally, measured from the attestation.
            const { data: lastAway } = await supabase.from('compliance_audit').select('triggered_at').eq('user_id', session.user.id).eq('trigger_type', 'away_gap_attested').order('triggered_at', { ascending: false }).limit(1).maybeSingle()
            const candidates = [lastLog?.log_date, lastAway ? String(lastAway.triggered_at).split('T')[0] : null, String(p.protocol_start_date).split('T')[0]].filter(Boolean)
            const anchor = candidates.sort().reverse()[0]
            const [ay, am, ad] = anchor.split('-').map(Number)
            const anchorD = new Date(ay, am - 1, ad)
            const nowD = new Date(); const todayD = new Date(nowD.getFullYear(), nowD.getMonth(), nowD.getDate())
            const silent = Math.floor((todayD - anchorD) / 86400000) - 1  // full days between last log and today
            if (silent >= AWAY_GATE.SOFT_MIN) setAwayGap({ days: silent, hard: silent >= AWAY_GATE.HARD_MIN })
          }
        }
      } catch (e) {}

      // The Record stats (weeks 1-4 card)
      try {
        if (p?.protocol_start_date) {
          const pStart = String(p.protocol_start_date).split('T')[0]
          const { count: dCount } = await supabase.from('daily_factors').select('id', { count: 'exact', head: true }).eq('user_id', session.user.id).gte('log_date', pStart)
          const { count: eCount } = await supabase.from('symptom_logs').select('id', { count: 'exact', head: true }).eq('user_id', session.user.id).gte('logged_at', pStart)
          setRecordStats({ days: dCount || 0, events: eCount || 0 })
        }
      } catch (e) {}

      // Has the user done today's daily check-in?
      try {
        const todayStr = todayLocal()
        const { data: df } = await supabase.from('daily_factors').select('id').eq('user_id', session.user.id).eq('log_date', todayStr).maybeSingle()
        setDailyDone(!!df)
      } catch (e) {}

      // Unread admin messages count (for the nav badge)
      try {
        const { count: umCount } = await supabase.from('tickets').select('id', { count: 'exact', head: true }).eq('user_id', session.user.id).eq('unread_for_user', true)
        setUnreadMsgs(umCount || 0)
      } catch (e) {}

      const { data: c } = await supabase.from('weekly_checkins').select('*').eq('user_id', session.user.id).order('submitted_at', { ascending: false }).limit(8)
      if (c) setCheckins(c)

      // Active reintro cycle (verdict survey id + verdict-ready detection for the hero)
      {
        const { data: ar } = await supabase.from('reintroduction_results')
          .select('id, food, started_at, exposure_days_completed, washout_started_at')
          .eq('user_id', session.user.id).is('verdict', null)
          .order('started_at', { ascending: false }).limit(1).maybeSingle()
        setActiveReintroId(ar?.id || null)
        setActiveCycleLite(ar || null)
      }

      const { data: comp } = await supabase.from('daily_compliance').select('*').eq('user_id', session.user.id).gte('date', localDateOffset(-10)).order('date', { ascending: false })
      // Away-on-plan days (user's word, counted): build the date set from away_gap_attested audits
      try {
        const { data: aways } = await supabase.from('compliance_audit').select('triggered_at, admin_note').eq('user_id', session.user.id).eq('trigger_type', 'away_gap_attested')
        const dates = new Set()
        for (const a of (aways || [])) {
          const m = /^(\d+) silent days/.exec(a.admin_note || '')
          const n = m ? parseInt(m[1], 10) : 0
          if (n > 0 && a.triggered_at) {
            const end = new Date(String(a.triggered_at).split('T')[0] + 'T12:00:00')
            for (let i = 1; i <= n; i++) {
              const d = new Date(end); d.setDate(end.getDate() - i)
              dates.add(localDateString(d))
            }
          }
        }
        setAwayDates(dates)
      } catch (e) {}

      // Last-10-days factor logs for the "your week" strip (descriptive mirror only)
      try {
        const { data: wf } = await supabase.from('daily_factors')
          .select('log_date, sleep, stress, hydration, drinks')
          .eq('user_id', session.user.id)
          .gte('log_date', localDateOffset(-120))
        setWeekFactors(wf || [])
      } catch (e) {}

      if (comp) {
        setComplianceData(comp)
        // Consecutive CALENDAR days, not consecutive rows (QA 2026-10-24 #5):
        // unlogged gap days have no rows, so 3 NOs spread across weeks used to
        // read as "three days in a row". Adjacent dates only.
        let nos = 0
        let expected = null
        for (const entry of comp) {
          if (entry.response !== 'NO') break
          if (expected && entry.date !== expected) break
          nos++
          const [ey, em, ed] = String(entry.date).split('-').map(Number)
          const prev = new Date(ey, em - 1, ed - 1)
          expected = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}-${String(prev.getDate()).padStart(2, '0')}`
        }
        setConsecutiveNOs(nos)
      }

      // Newest pending audit, never maybeSingle (QA 2026-10-24 #4): two pending
      // rows made maybeSingle error -> the pending card vanished AND the 3-NOs
      // card re-armed, inviting duplicate audits.
      const { data: audits } = await supabase.from('compliance_audit').select('*').eq('user_id', session.user.id).eq('status', 'pending_admin_review').order('triggered_at', { ascending: false }).limit(1)
      setPendingAudit(!!(audits && audits.length))

      // The guidance loop closes here (QA 2026-10-24 #1): when Alex responds
      // to an audit, his outcome + note surface as a dashboard card instead of
      // the pending card silently vanishing into nothing. Shown for 7 days.
      try {
        const { data: resp } = await supabase.from('compliance_audit')
          .select('admin_outcome, admin_note, responded_at, trigger_type')
          .eq('user_id', session.user.id).eq('status', 'responded')
          .or('trigger_type.is.null,trigger_type.neq.away_gap_attested')
          .order('responded_at', { ascending: false }).limit(1)
        const r = resp && resp[0]
        if (r && r.responded_at && (Date.now() - new Date(r.responded_at).getTime()) < 7 * 86400000) {
          setAuditGuidance(r)
        } else setAuditGuidance(null)
      } catch (e) { setAuditGuidance(null) }

      if (p?.program_phase === 'elimination' || p?.program_phase === 'reintroduction') {
        const { data: c } = await supabase.from('weekly_checkins').select('submitted_at, answers, week_number').eq('user_id', session.user.id).order('submitted_at', { ascending: false }).limit(8)

        // Calculate check-in availability based on protocol_start_date
        const protocolStart = p?.protocol_start_date
        if (protocolStart) {
          // Parse as LOCAL date — same as calculateProtocolDay — so both agree
          const [sy, sm, sd] = protocolStart.split('T')[0].split('-').map(Number)
          const startDate = new Date(sy, sm - 1, sd)
          const now = new Date()
          const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate())
          // daysSinceStart aligns with protocol day: start date itself = day 1
          const daysSinceStart = Math.round((todayLocal - startDate) / (1000 * 60 * 60 * 24)) + 1

          // Windows open every 7 days: day 7, 14, 21, 28...
          // On-time window is 48 hours (days 7-8, 14-15...). If missed, the
          // check-in stays available as LATE until the next window opens —
          // users are never silently stranded with a gap week.
          if (daysSinceStart >= 7) {
            const currentWindowWeek = Math.floor(daysSinceStart / 7) // which week we're in
            const windowOpenDay = currentWindowWeek * 7 // day 7, 14, 21...
            const windowCloseDay = windowOpenDay + 1 // 48 hours = 2 calendar days

            const inWindow = daysSinceStart >= windowOpenDay && daysSinceStart <= windowCloseDay

            // A window is complete if a check-in exists for that week number.
            // Uses the stored week_number (not timestamps) so manual
            // protocol_start_date adjustments during testing behave sanely.
            const completedThisWindow = c && c.some(checkin => checkin.week_number === currentWindowWeek)

            // Late grace: anywhere past the 48h window but before the next
            // window, the current week's check-in is still completable
            setWeeklyDue(!completedThisWindow)
            setCheckinLate(!inWindow && !completedThisWindow)
            setActiveCheckinWeek(currentWindowWeek)
          } else {
            setWeeklyDue(false)
            setCheckinLate(false)
          }
        } else {
          setWeeklyDue(false)
        }

        // No improvement detection — after week 4 with high compliance
        const protocolDayNum = p?.protocol_start_date
          ? protocolDay(p.protocol_start_date)
          : 0

        if (protocolDayNum >= 28 && c && c.length >= 4) {
          // Measure only dimensions with BOTH a baseline and a latest reading
          // (QA 2026-10-23 #6): the old `: 100` fallbacks scored missing data
          // as 100% improvement, so sparse-data users — the exact customers
          // this net exists for — could never trip it. No measurable
          // dimension at all = insufficient data, not improvement.
          const dims = []
          const baselineBloating = p?.baseline_bloating
          const baselineEnergy = p?.baseline_energy
          const latestBloating = c[0]?.answers?.bloating
          const latestEnergy = c[0]?.answers?.energy
          if (baselineBloating && latestBloating) dims.push(((baselineBloating - latestBloating) / baselineBloating) * 100)
          if (baselineEnergy && latestEnergy) dims.push(((latestEnergy - baselineEnergy) / baselineEnergy) * 100)
          const avgImprovement = dims.length ? dims.reduce((a, b) => a + b, 0) / dims.length : null

          const highComplianceCount = c.filter(checkin =>
            checkin.answers?.compliance === 'Fully' || checkin.answers?.compliance === 'Mostly'
          ).length

          if (avgImprovement !== null && avgImprovement < 15 && highComplianceCount >= 3) {
            const { data: existingAudit } = await supabase
              .from('compliance_audit')
              .select('id')
              .eq('user_id', session.user.id)
              .eq('trigger_type', 'no_improvement')
              .gte('triggered_at', new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString())
              .maybeSingle()

            if (!existingAudit) {
              await supabase.from('compliance_audit').insert({
                user_id: session.user.id,
                triggered_at: new Date().toISOString(),
                trigger_type: 'no_improvement',
                hardest_parts: [],
                status: 'pending_admin_review',
              })
            }
          }
        }
      }
      // Check for milestone messages
      if (p?.protocol_start_date) {
        try {
          checkMilestones(p, resolvedLab, c || [])
        } catch (e) {}
      }

    } catch (e) {}
    setLoading(false)
  }
  const checkMilestones = async (p, lab, c) => {
    if (!p?.protocol_start_date) return
    // Tone-awareness: if the latest weekly check-in shows symptoms broadly worsening
    // vs baseline, suppress celebration confetti (milestone messages still show —
    // reaching day 14 on a rough week deserves acknowledgment, not a party).
    const roughWeek = (() => {
      try {
        const latest = (c || []).slice().sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at))[0]
        if (!latest?.answers) return false
        const lowerBetter = ['bloating', 'gas', 'reflux']
        const higherBetter = ['digestive', 'energy', 'clarity', 'afternoon', 'sleep', 'wellbeing']
        let worse = 0, better = 0
        for (const k of [...lowerBetter, ...higherBetter]) {
          const bl = p['baseline_' + k]; const cur = latest.answers[k]
          if (bl == null || cur == null) continue
          const improved = lowerBetter.includes(k) ? cur < bl : cur > bl
          const worsened = lowerBetter.includes(k) ? cur > bl : cur < bl
          if (improved) better++; else if (worsened) worse++
        }
        return worse > better && worse >= 2
      } catch { return false }
    })()
    const celebrate = () => {
      if (roughWeek) return
      setShowConfetti(true)
      setTimeout(() => setShowConfetti(false), 3500)
    }
    const name = session?.user?.user_metadata?.full_name?.split(' ')[0] || 'there'
    const day = protocolDay(p.protocol_start_date)

    // Day 1 — message stays visible for all of day 1 (re-shown on every load),
    // but the confetti celebration only fires the first time.
    if (day === 1) {
      try {
        // Generate ONCE and persist the text: regenerating on every load
        // produced a different briefing each render (finding 8, round 1).
        const sm = normalizeMilestones(p.shown_milestones)
        let msg = sm.day1_text
        if (!msg) {
          msg = await generateDay1Message({ name, profile: p, labResult: lab })
          try { await supabase.from('profiles').update({ shown_milestones: serializeMilestones({ ...sm, day1_text: msg }) }).eq('id', session.user.id) } catch (e) {}
        }
        setMilestoneMessage(msg)
        setMilestoneKey('day1')
        const shown = await checkMilestoneShown(supabase, session.user.id, 'day1')
        if (!shown) {
          celebrate()
          await markMilestoneShown(supabase, session.user.id, 'day1')
        }
      } catch (e) {}
    }
    // Windows, not exact days (QA 2026-10-23 #1): a customer who doesn't open
    // the app on precisely day 3/14/28 used to lose that milestone forever.
    // Each window still fires once (the shown-once guard), on the first visit
    // inside it — the same pattern day 57 already used.
    else if (day >= 3 && day < 14) {
      const shown = await checkMilestoneShown(supabase, session.user.id, 'day3')
      if (!shown) {
        try {
          const msg = await generateDay3Message({ name, profile: p, labResult: lab })
          setMilestoneMessage(msg)
          setMilestoneKey('day3')
          await markMilestoneShown(supabase, session.user.id, 'day3')
        } catch (e) {}
      }
    }
    else if (day >= 14 && day < 28) {
      const shown = await checkMilestoneShown(supabase, session.user.id, 'day14')
      if (!shown) {
        try {
          const msg = await generateDay14Message({ name, profile: p, checkins: c })
          setMilestoneMessage(msg)
          setMilestoneKey('day14')
          celebrate()
          await markMilestoneShown(supabase, session.user.id, 'day14')
        } catch (e) {}
      }
    }
    else if (day >= 28 && day < 57) {
      const shown = await checkMilestoneShown(supabase, session.user.id, 'day28')
      if (!shown) {
        try {
          const msg = await generateDay28Message({ name, profile: p, checkins: c })
          setMilestoneMessage(msg)
          setMilestoneKey('day28')
          celebrate()
          await markMilestoneShown(supabase, session.user.id, 'day28')
        } catch (e) {}
      }
    }
    else if (day >= 57 && day < 113) {
      const shown = await checkMilestoneShown(supabase, session.user.id, 'day57')
      if (!shown) {
        try {
          const msg = await generateDay57Message({ name, profile: p, labResult: lab })
          setMilestoneMessage(msg)
          setMilestoneKey('day57')
          celebrate()
          await markMilestoneShown(supabase, session.user.id, 'day57')
        } catch (e) {}
      }
    }
    // Day 113 / day 169 tier-unlock milestones are RETIRED: every track now
    // runs one frequency-ordered queue that unlocks whole at day 57, so a
    // "Moderate tier unlocked" message would announce a system that no
    // longer exists. (generateModerateUnlockMessage / generateHighUnlockMessage
    // kept in aiInsights for history; no caller.)
  }

  // Streak = consecutive calendar days with a completed daily check-in.
  // Walks real DATES (a missed day breaks it — missing rows used to be
  // skipped over, inflating the count), and counts doing-the-survey, not
  // answering YES — an honest "I slipped" day still continues the streak.
  const cleanDays = (() => {
    const logged = new Set(weekFactors.map(f => f.log_date))
    let count = 0
    const start = new Date()
    if (!logged.has(localDateString(start))) start.setDate(start.getDate() - 1)  // today pending ≠ broken
    // The streak begins with the protocol: check-ins logged before day 1
    // (setup week, pre-decision) never inflate it (finding 9).
    const streakFloor = profile?.protocol_start_date ? String(profile.protocol_start_date).split('T')[0] : null
    for (let i = 0; i < 365; i++) {
      const d = new Date(start); d.setDate(start.getDate() - i)
      const ds = localDateString(d)
      if (streakFloor && ds < streakFloor) break
      if (logged.has(ds)) count++
      else if (awayDates.has(ds)) continue  // away, on plan: pauses the streak, never resets it
      else break
    }
    return count
  })()

  // Calculate current day and phase from protocol_start_date
  // Only counts if intake is completed AND lab results approved
  const calculateProtocolDay = () => {
    if (!profile?.protocol_start_date) return 0
    if (!profile?.intake_completed_at) return 0
    if (profile?.program_phase === 'pending_review' || profile?.program_phase === 'awaiting_results') return 0
    // Parse as LOCAL date (new Date('YYYY-MM-DD') is UTC and shifts the day in US timezones)
    const [y, m, d] = profile.protocol_start_date.split('T')[0].split('-').map(Number)
    const start = new Date(y, m - 1, d)
    const now = new Date()
    const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const diffDays = Math.round((todayLocal - start) / (1000 * 60 * 60 * 24)) + 1
    // Day 0 = approved, starts tomorrow. Never negative.
    return Math.max(0, diffDays)
  }

  const calculatePhase = (day) => {
    if (!profile?.protocol_start_date) return profile?.program_phase || null
    if (!profile?.intake_completed_at) return profile?.program_phase || null
    if (day <= 56) return 'elimination'
    return 'reintroduction'
  }

  const currentDay = calculateProtocolDay()
  const currentWeek = Math.ceil(currentDay / 7)
  const calculatedPhase = calculatePhase(currentDay)
  // Only foods that will actually be trialed count toward the reintro span
  // (QA 2026-11-03 #2): exclude never-frequency foods, matching the suspect
  // board, so the hero clock and the board can't contradict each other.
  const testableFoodCount = (() => {
    const freq = profile?.food_frequency || {}
    const foods = labResult?.foods || []
    const n = foods.filter(f => freq[f.name] !== 'never').length
    return n > 0 ? n : (foods.length || 8)
  })()
  const reintroSpanDays = testableFoodCount * 14
  const eliminationProgressPct = profile?.protocol_start_date ? Math.min((currentDay / 56) * 100, 100) : 0
  const daysUntilReintro = profile?.protocol_start_date ? Math.max(57 - currentDay, 0) : null

  const today = todayLocal()
  const todayEntry = complianceData.find(c => c.date === today)
  // One compassion card at a time: once three days off plan trigger the
  // audit, it subsumes the daily slip-up card instead of stacking on it.
  const showAuditCard = consecutiveNOs >= 3 && !pendingAudit
  const showSlipupCard = todayEntry?.response === 'NO' && !pendingAudit && !showAuditCard
  const showIntakeCard = !profile?.intake_completed_at
  const showLabCard = profile?.intake_completed_at && !labResult
  const showPendingLabCard = labResult?.status === 'pending_review'
  const showCheckinCard = weeklyDue && !showIntakeCard && !showLabCard && !showSlipupCard && !showAuditCard
  // Common-track user who has been assigned but hasn't chosen a tier or declined yet.
  const needsCommonDecision = profile?.protocol_track === 'common'
    && profile?.track_decision !== 'active'
    && profile?.track_decision !== 'declined'
  // User who declined the protocol and is in self-tracking mode.
  const isTracking = profile?.track_decision === 'declined'
  const verdictReadyFood = (() => {
    try {
      if (!activeCycleLite?.washout_started_at) return null
      const [y, m, d] = String(activeCycleLite.washout_started_at).split('T')[0].split('-').map(Number)
      const ws = new Date(y, m - 1, d)
      const now = new Date()
      const washoutDay = Math.floor((new Date(now.getFullYear(), now.getMonth(), now.getDate()) - ws) / 86400000) + 1
      return washoutDay > 11 ? activeCycleLite.food : null
    } catch (e) { return null }
  })()

  // One stale-cycle fact (QA 2026-10-23 #3 folded the twin IIFEs that computed
  // this twice): the active cycle's food when it stalled pre-washout (>5 days
  // without 3 exposures), else null.
  const cycleStale = (() => {
    try {
      if (!activeCycleLite?.started_at || activeCycleLite.washout_started_at) return null
      if ((activeCycleLite.exposure_days_completed || 0) >= 3) return null
      const [y, m, d] = String(activeCycleLite.started_at).split('T')[0].split('-').map(Number)
      const cs = new Date(y, m - 1, d)
      const now = new Date()
      const cd = Math.floor((new Date(now.getFullYear(), now.getMonth(), now.getDate()) - cs) / 86400000) + 1
      return cd > 5 ? activeCycleLite.food : null
    } catch (e) { return null }
  })()

  const betweenCycles = calculatedPhase === 'reintroduction' && !activeCycleLite && !verdictReadyFood

  const cycleLine = (() => {
    if (!activeCycleLite?.started_at) return null
    try {
      const [y, m, d] = String(activeCycleLite.started_at).split('T')[0].split('-').map(Number)
      const cs = new Date(y, m - 1, d)
      const now = new Date()
      const rawCd = Math.floor((new Date(now.getFullYear(), now.getMonth(), now.getDate()) - cs) / 86400000) + 1
      const cd = Math.min(rawCd, 14)
      if (cd < 1) return `${activeCycleLite.food.toUpperCase()} CYCLE · STARTS TOMORROW`
      if (cd > 5 && (activeCycleLite.exposure_days_completed || 0) < 3 && !activeCycleLite.washout_started_at) return `${activeCycleLite.food.toUpperCase()} CYCLE · NEEDS A RESTART · SEE THE REINTRO TAB`
      const ph = activeCycleLite.washout_started_at ? 'WASHOUT' : 'EXPOSURE'
      return `${activeCycleLite.food.toUpperCase()} CYCLE · DAY ${cd} OF 14 · ${ph}`
    } catch (e) { return null }
  })()

  const cockpitActive = (calculatedPhase === 'elimination' || calculatedPhase === 'reintroduction') && profile?.protocol_start_date && currentDay >= 1 && !needsCommonDecision && !isTracking && !showIntakeCard && !showLabCard && !showPendingLabCard
  const showReintroCard = calculatedPhase === 'reintroduction' && profile?.current_reintro_day >= 14

  const phaseLabel = calculatedPhase === 'elimination' ? 'Elimination'
    : calculatedPhase === 'reintroduction' ? 'Reintroduction'
    : profile?.program_phase === 'pending_review' ? 'Pending'
    : 'Setup'

  const showAvoidingList = labResult?.foods && labResult.status !== 'pending_review'

  // Exposure-honest avoiding list (mirrors netlify/functions/household-card.js):
  // the trial food leaves the list during exposure, because it is deliberately
  // back on the plate, and returns during washout. Verdict-aware: a food whose
  // latest cycle verdict is Safe has left elimination entirely; Avoid and Limit
  // foods stay listed carrying their earned verdict stamp.
  const latestCycleByFood = (() => {
    const map = {}
    ;[...reintroRows].sort((a, b) => String(a.started_at || '').localeCompare(String(b.started_at || ''))).forEach(r => { map[(r.food || '').toLowerCase()] = r })
    return map
  })()
  const activeExposureFood = (activeCycleLite && !activeCycleLite.washout_started_at) ? (activeCycleLite.food || '').toLowerCase() : null
  const avoidedFoods = (labResult?.foods || []).filter(f => {
    const key = (f.name || '').toLowerCase()
    if (latestCycleByFood[key]?.verdict === 'Safe') return false
    if (activeExposureFood === key && !latestCycleByFood[key]?.verdict) return false
    return true
  })

  if (screen === 'intake') return <IntakeSurvey session={session} onBack={() => setScreen('dashboard')} onComplete={() => setScreen('dashboard')} />
  if (screen === 'labresults') return <LabResults session={session} onBack={() => setScreen('dashboard')} onComplete={() => setScreen('dashboard')} />
  if (screen === 'checkin') return <WeeklyCheckin session={session} weekNumber={activeCheckinWeek || currentWeek || 1} profile={profile} currentFoods={labResult?.foods?.map(f => f.name) || []} phase={calculatedPhase || 'elimination'} onBack={() => { setTab('history'); setScreen('checkin-history') }} onComplete={() => { setWeeklyDue(false); setTab('home'); setScreen('dashboard') }} />
  if (screen === 'daily-checkin') return <DailyCheckin session={session} profile={profile} onBack={() => setScreen('dashboard')} onComplete={() => { setDailyDone(true); setScreen('dashboard') }} />
  if (screen === 'slipup') return <SlipupSurvey session={session} profile={profile} labResult={labResult} currentDay={currentDay} onBack={() => setScreen('dashboard')} onComplete={() => setScreen('dashboard')} />
  if (screen === 'audit') return <ComplianceAudit session={session} eliminatedFoods={labResult?.foods?.map(f => f.name) || []} onBack={() => setScreen('dashboard')} onComplete={() => setScreen('dashboard')} />
  if (screen === 'reintro-survey') return <ReintroductionSurvey session={session} food={profile?.current_reintro_food || 'Eggs'} cycleNumber={profile?.reintro_cycle || 1} baselineScores={{ bloating: profile?.baseline_bloating, energy: profile?.baseline_energy }} symptoms={profile?.symptoms || []} profile={profile} activeReintroId={activeReintroId} onBack={() => setScreen('dashboard')} onComplete={() => setScreen('dashboard')} />

  // Shared tab navigation (top tabs on desktop, bottom bar on phones)
  const goTab = (t) => {
    window.scrollTo(0, 0)
    setMoreOpen(false)
    setTab(t.toLowerCase().replace(' ', '-'))
    if (t === 'History') setScreen('checkin-history')
    else if (t === 'Verdicts') setScreen('food-map')
    else if (t === 'Reintro') setScreen('reintro-tab')
    else if (t === 'Ask Sensify') setScreen('ask-sensify')
    else if (t === 'Maintain') setScreen('maintain')
    else setScreen('dashboard')
  }
  const maintainVisible = (() => {
    if (isAdmin) return true
    if (!profile?.protocol_start_date) return false
    const sd = new Date(profile.protocol_start_date + 'T00:00:00')
    const now = new Date(); const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    return Math.round((todayLocal - sd) / (1000 * 60 * 60 * 24)) + 1 >= 57
  })()

  // Reach-your-program failure: never the setup screen (QA 2026-11-03 #1).
  if (loadError) {
    return (
      <div className="snfy-app">
        <style>{css}</style>
        <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '24px', gap: 12 }}>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 22, fontWeight: 300, color: 'var(--ink)' }}>Can't reach your program.</div>
          <div style={{ fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.6, maxWidth: 320 }}>Your data is safe — we just couldn't load it right now. Check your connection and try again.</div>
          <button className="snfy-btn" style={{ maxWidth: 220 }} onClick={() => loadData()}>Try again</button>
        </div>
      </div>
    )
  }

  return (
    <div className="snfy-app">
      <style>{css}</style>

      <nav className="snfy-nav">
        <div className="snfy-logo">Sensify<span style={{ color: '#8BAE8A', fontStyle: 'normal' }}>.</span></div>
        <div className="snfy-nav-tabs">
          {['Home', 'Reintro', 'History', 'Verdicts', 'Ask Sensify', 'Maintain'].filter(t => t !== 'Maintain' || maintainVisible).map(t => (
            <button key={t} className={`snfy-tab${tab === t.toLowerCase().replace(' ', '-') ? ' active' : ''}`} onClick={() => goTab(t)}>{t}</button>
          ))}
        </div>
        <div className="snfy-nav-right">
          <div className="snfy-phase-pill">{phaseLabel}</div>
          {!isAdmin && (
            <button className="snfy-msg-btn" onClick={() => { setTab('messages'); setScreen('messages') }} title="Support">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3D5C3C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              {unreadMsgs > 0 && <span className="snfy-msg-badge">{unreadMsgs}</span>}
            </button>
          )}
          {isAdmin && <button className="snfy-signout" onClick={onAdmin} style={{ color: '#3D5C3C', fontWeight: 500 }}>Admin</button>}
          <button className="snfy-signout" onClick={onLogout}>Sign out</button>
        </div>
      </nav>

      {/* BOTTOM TAB BAR — phones only (design item 5). Thumb-reach nav;
          History, Maintain and Sign out live behind More. */}
      <div className="snfy-bottombar">
        {[['Home', 'home'], ['Reintro', 'reintro'], ['Verdicts', 'verdicts'], ['Ask Sensify', 'ask-sensify']].map(([t, key]) => (
          <button key={key} className={`snfy-bb-btn${tab === key && !moreOpen ? ' active' : ''}`} onClick={() => goTab(t)}>
            <span className="snfy-bb-dot" />{t === 'Ask Sensify' ? 'Ask' : t}
          </button>
        ))}
        <button className={`snfy-bb-btn${moreOpen || tab === 'history' || tab === 'maintain' ? ' active' : ''}`} onClick={() => setMoreOpen(v => !v)}>
          <span className="snfy-bb-dot" />More
        </button>
      </div>
      {moreOpen && (
        <div className="snfy-more-sheet">
          <button className="snfy-more-item" onClick={() => goTab('History')}>History</button>
          {maintainVisible && <button className="snfy-more-item" onClick={() => goTab('Maintain')}>Maintain</button>}
          {!isAdmin && <button className="snfy-more-item" onClick={() => { setMoreOpen(false); setTab('messages'); setScreen('messages') }}>Support</button>}
          <button className="snfy-more-item" onClick={onLogout}>Sign out</button>
        </div>
      )}

      {screen === 'checkin-history' ? (
        <CheckinHistory
          session={session}
          profile={profile}
          weeklyDue={weeklyDue}
          onStartCheckin={() => setScreen('checkin')}
        />
      ) : screen === 'food-map' ? (
        <FoodMap
          session={session}
          profile={profile}
          labResult={labResult}
        />
      ) : screen === 'ask-sensify' ? (
        <div style={{ height: 'calc(100vh - 60px)' }}>
          {(() => {
            // Ask Sensify is locked until lab results are back, because before
            // that it has no Food Map to reason from and could imply results
            // that don't exist yet. Tracking (declined) users are allowed in
            // (observation mode handles their no-Food-Map case safely).
            const resultsBack = (labResult && labResult.status === 'approved') || profile?.track_decision === 'declined' || profile?.program_phase === 'elimination' || profile?.program_phase === 'reintroduction' || profile?.program_phase === 'complete' || profile?.program_phase === 'tracking'
            if (!resultsBack) {
              return (
                <div style={{ height: '100%', overflowY: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '28px 20px' }}>
                  <style>{`
                    @keyframes askDotPulse { 0%, 60%, 100% { opacity: 0.25; transform: translateY(0); } 30% { opacity: 1; transform: translateY(-3px); } }
                    @keyframes askMsgIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
                    @keyframes askGlow { 0%, 100% { opacity: 0.05; } 50% { opacity: 0.11; } }
                    @keyframes askRise { from { opacity: 0; transform: translateY(18px) scale(0.985); } to { opacity: 1; transform: translateY(0) scale(1); } }
                  `}</style>
                  <div style={{ maxWidth: 470, width: '100%' }}>

                    <div style={{ textAlign: 'center', marginBottom: 24, animation: 'askMsgIn 0.5s ease both' }}>
                      <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 10, letterSpacing: '2.5px', color: '#C9A227', marginBottom: 12 }}>LOCKED · UNLOCKS WITH YOUR RESULTS</div>
                      <div style={{ fontFamily: 'Fraunces, serif', fontSize: 36, fontWeight: 300, color: '#1C1C1C', lineHeight: 1.1 }}>A guide that knows<br /><em style={{ fontStyle: 'italic', color: '#3D5C3C' }}>your body.</em></div>
                    </div>

                    <div style={{ position: 'relative', background: '#22301F', borderRadius: 22, padding: '26px 26px 22px', overflow: 'hidden', boxShadow: '0 24px 60px rgba(34,48,31,0.35)', animation: 'askRise 0.7s cubic-bezier(0.16,1,0.3,1) both' }}>
                      <div style={{ position: 'absolute', top: -70, right: -70, width: 240, height: 240, borderRadius: '50%', background: '#8BAE8A', animation: 'askGlow 5s ease-in-out infinite', pointerEvents: 'none' }} />
                      <div style={{ position: 'absolute', bottom: -90, left: -60, width: 200, height: 200, borderRadius: '50%', background: '#E8941F', opacity: 0.05, pointerEvents: 'none' }} />

                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, position: 'relative' }}>
                        <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 9.5, letterSpacing: '1.5px', color: '#8BAE8A' }}>SENSIFY · ASSISTANT</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#E8941F' }} />
                          <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 8.5, letterSpacing: '1px', color: 'rgba(250,248,244,0.55)' }}>PREVIEW</span>
                        </div>
                      </div>

                      <div style={{ position: 'relative', animation: 'askMsgIn 0.6s ease 0.5s both' }}>
                        <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 9, fontWeight: 700, letterSpacing: '1.2px', color: 'rgba(250,248,244,0.45)', marginBottom: 6 }}>YOU</div>
                        <div style={{ fontSize: 15.5, lineHeight: 1.6, color: 'rgba(250,248,244,0.92)', fontWeight: 300 }}>Dinner at a Thai place tonight. What should I order and what should I skip?</div>
                      </div>

                      <div style={{ position: 'relative', borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 16, paddingTop: 16, animation: 'askMsgIn 0.6s ease 1.1s both' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 7 }}>
                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#8BAE8A', boxShadow: '0 0 10px rgba(139,174,138,0.8)' }} />
                          <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 9, fontWeight: 700, letterSpacing: '1.2px', color: '#8BAE8A' }}>SENSIFY</span>
                        </div>
                        <div style={{ fontSize: 15.5, lineHeight: 1.7, color: '#FAF8F4', fontWeight: 300 }}>Good news: most curries are built on coconut milk, which is clear on your map. Watch the <span style={{ background: 'rgba(214,69,69,0.22)', border: '1px solid rgba(214,69,69,0.45)', padding: '2px 9px', borderRadius: 20, color: '#F2A0A0', fontWeight: 500, boxShadow: '0 0 14px rgba(214,69,69,0.25)' }}>soy</span> in the stir-fries and ask about <span style={{ background: 'rgba(232,148,31,0.18)', border: '1px solid rgba(232,148,31,0.45)', padding: '2px 9px', borderRadius: 20, color: '#F2C078', fontWeight: 500, boxShadow: '0 0 14px rgba(232,148,31,0.22)' }}>peanut</span> garnishes. Pad see ew is the one to skip.</div>
                      </div>

                      <div style={{ position: 'relative', borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 16, paddingTop: 16, animation: 'askMsgIn 0.6s ease 1.9s both' }}>
                        <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 9, fontWeight: 700, letterSpacing: '1.2px', color: 'rgba(250,248,244,0.45)', marginBottom: 6 }}>YOU</div>
                        <div style={{ fontSize: 15.5, lineHeight: 1.6, color: 'rgba(250,248,244,0.92)', fontWeight: 300 }}>What about the mango sticky rice?</div>
                      </div>

                      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, animation: 'askMsgIn 0.6s ease 2.5s both' }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#8BAE8A', boxShadow: '0 0 10px rgba(139,174,138,0.8)' }} />
                        <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 9, fontWeight: 700, letterSpacing: '1.2px', color: '#8BAE8A' }}>SENSIFY</span>
                        <span style={{ display: 'inline-flex', gap: 4, marginLeft: 3 }}>
                          {[0, 1, 2].map(i => (
                            <span key={i} style={{ width: 5.5, height: 5.5, borderRadius: '50%', background: '#8BAE8A', animation: `askDotPulse 1.2s ease-in-out ${i * 0.18}s infinite` }} />
                          ))}
                        </span>
                      </div>

                      <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                        {['MENUS', 'LABELS', 'RECIPES', 'PHOTOS'].map(w => (
                          <div key={w} style={{ fontFamily: 'DM Mono, monospace', fontSize: 8.5, letterSpacing: '1.5px', color: 'rgba(250,248,244,0.45)' }}>{w}</div>
                        ))}
                      </div>
                    </div>

                    <div style={{ fontSize: 13, color: '#7A7A72', lineHeight: 1.65, textAlign: 'center', maxWidth: 360, margin: '18px auto 0', animation: 'askMsgIn 0.6s ease 0.3s both' }}>Answers come from <strong style={{ color: '#3D5C3C' }}>your</strong> lab results and <strong style={{ color: '#3D5C3C' }}>your</strong> map, never generic advice. Until your results are in, there's nothing to base them on. The wait is what makes them yours.</div>

                  </div>
                </div>
              )
            }
            return <AskSensify session={session} />
          })()}
        </div>
      ) : screen === 'messages' ? (
        <div style={{ height: 'calc(100vh - 60px)' }}>
          <Support session={session} onUnreadChange={(n) => setUnreadMsgs(n)} />
        </div>
      ) : screen === 'maintain' ? (
        <div style={{ height: 'calc(100vh - 60px)', overflowY: 'auto' }}>
          <MaintainHub session={session} profile={profile} isAdmin={isAdmin} />
        </div>
      ) : screen === 'reintro-tab' ? (
        <ReintroTab
          session={session}
          profile={profile}
          labResult={labResult}
          currentDay={currentDay}
          onOpenDailyCheckin={() => setScreen('daily-checkin')}
          onStartVerdictSurvey={(food) => {
            setScreen('reintro-survey')
          }}
        />
      ) : loading ? (
        <div className="snfy-loading">Loading your program...</div>
      ) : (<>
        {cockpitActive && (() => {
          // Presentation only: pick the ONE clock this hero leads with. While a
          // cycle is running normally, the cycle clock is the big line and the
          // program day demotes to a quiet micro line; stale and not-yet-started
          // cycles keep their existing micro-line messaging.
          const runningCycle = (() => {
            try {
              if (!activeCycleLite?.started_at || cycleStale) return null
              const [y, m, d] = String(activeCycleLite.started_at).split('T')[0].split('-').map(Number)
              const cs = new Date(y, m - 1, d)
              const nowD = new Date()
              const rawCd = Math.floor((new Date(nowD.getFullYear(), nowD.getMonth(), nowD.getDate()) - cs) / 86400000) + 1
              if (rawCd < 1) return null
              return { food: activeCycleLite.food, cd: Math.min(rawCd, 14), phase: activeCycleLite.washout_started_at ? 'Washout' : 'Exposure' }
            } catch (e) { return null }
          })()
          const reintroSpan = reintroSpanDays
          const clock = runningCycle
            ? { pct: (runningCycle.cd / 14) * 100, left: 'Day 1', right: 'Day 14 · verdict' }
            : calculatedPhase === 'reintroduction'
              ? { pct: ((currentDay - 56) / reintroSpan) * 100, left: 'Day 1', right: `Day ${reintroSpan} · map complete` }
              : { pct: (currentDay / 56) * 100, left: 'Day 1', right: 'Day 56 · reintroduction' }
          const pct = Math.min(100, Math.max(2, Math.round(clock.pct)))
          return (
          <div className="snfy-heroband">
            <div className="sn-hero sn-rise" style={{ padding: '24px 26px 22px' }}>
              <div className="snfy-hero-top" style={{ marginBottom: 18 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <svg width="62" height="62" viewBox="0 0 62 62" aria-hidden="true">
                    <circle cx="31" cy="31" r="26" fill="none" stroke="rgba(250,248,244,0.14)" strokeWidth="5" />
                    <circle className="snfy-hero-ring" cx="31" cy="31" r="26" fill="none" stroke="#8BAE8A" strokeWidth="5" strokeLinecap="round" strokeDasharray="163.4" strokeDashoffset="0" transform="rotate(-90 31 31)" style={{ '--sn-draw-from': 163.4, animation: 'snDraw 1.1s var(--ease) both' }} />
                    <text x="31" y="38" textAnchor="middle" fontFamily="Fraunces, Georgia, serif" fontWeight="300" fontSize="22" fill="#FAF8F4">{cleanDays > 0 ? cleanDays : '–'}</text>
                  </svg>
                  {/* A celebrated zero reads as a bug (QA 2026-10-23 #5): before the
                      first check-in the ring shows a dash and names what starts it. */}
                  <span className="sn-micro sn-micro-dark" style={{ textAlign: 'center', maxWidth: 86 }}>{cleanDays > 0 ? <>day streak{cleanDays >= 3 ? ', unbroken' : ''}</> : 'streak starts with tonight’s check-in'}</span>
                </div>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontSize: 12, color: 'rgba(250,248,244,0.5)', marginBottom: 4 }}>{getGreeting()}, {name}</div>
                  <div className="snfy-hero-big">
                    {profile?.program_phase === 'complete' ? 'Every verdict, earned.'
                      : betweenCycles ? 'Choose your next food.'
                      : runningCycle ? <>{runningCycle.food} cycle, day {runningCycle.cd} of 14.<span className="snfy-hero-phase">{runningCycle.phase}</span></>
                      : `Day ${calculatedPhase === 'reintroduction' ? currentDay - 56 : currentDay} of ${calculatedPhase === 'reintroduction' ? 'reintroduction' : 'elimination'}.`}
                  </div>
                  {runningCycle && <div className="sn-micro sn-micro-dark" style={{ marginTop: 8 }}>{calculatedPhase === 'reintroduction' ? 'Reintroduction' : 'Elimination'} · day {calculatedPhase === 'reintroduction' ? currentDay - 56 : currentDay}</div>}
                  {!runningCycle && cycleLine && <div className="sn-micro" style={{ color: '#8BAE8A', marginTop: 8 }}>{cycleLine}</div>}
                  {betweenCycles && <div className="sn-micro" style={{ color: '#8BAE8A', marginTop: 8 }}>REINTRODUCTION · DAY {currentDay - 56}{(currentDay - 56) <= reintroSpanDays ? ` OF ${reintroSpanDays}` : ''} · {reintroRows.some(r => r.verdict) ? 'CYCLE COMPLETE' : 'TESTING OPEN'}</div>}
                </div>
                {verdictReadyFood ? (
                  <button onClick={() => { window.scrollTo(0, 0); setTab('reintro'); setScreen('reintro-tab') }} style={{ background: '#C9A227', border: 'none', borderRadius: 'var(--r-md)', padding: '12px 19px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', boxShadow: '0 0 22px rgba(201,162,39,0.35)' }}>
                    <span style={{ fontSize: 13, color: '#22301F', fontWeight: 700 }}>Your {verdictReadyFood} verdict is ready</span>
                    <span style={{ fontSize: 13, color: '#22301F', fontWeight: 700 }}>{'\u2192'}</span>
                  </button>
                ) : cycleStale ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                    <button onClick={() => { window.scrollTo(0, 0); setTab('reintro'); setScreen('reintro-tab') }} style={{ background: '#D4894A', border: 'none', borderRadius: 'var(--r-md)', padding: '12px 19px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', boxShadow: '0 0 20px rgba(212,137,74,0.3)' }}>
                      <span style={{ fontSize: 13, color: '#22301F', fontWeight: 700 }}>Restart your {cycleStale.toLowerCase()} cycle</span>
                      <span style={{ fontSize: 13, color: '#22301F', fontWeight: 700 }}>{'\u2192'}</span>
                    </button>
                    {!dailyDone && <button onClick={() => setScreen('daily-checkin')} style={{ background: 'transparent', border: 'none', padding: 0, fontSize: 11.5, color: 'rgba(250,248,244,0.55)', textDecoration: 'underline', cursor: 'pointer' }}>{new Date().getHours() >= 17 ? 'or log tonight\u2019s check-in' : 'or log today\u2019s check-in'}</button>}
                  </div>
                ) : profile?.program_phase === 'complete' ? (
                  <button onClick={() => goTab('Verdicts')} style={{ background: '#8BAE8A', border: 'none', borderRadius: 'var(--r-md)', padding: '12px 19px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <span style={{ fontSize: 13, color: '#22301F', fontWeight: 600 }}>Your Food Map is complete</span>
                    <span style={{ fontSize: 13, color: '#22301F', fontWeight: 700 }}>{'\u2192'}</span>
                  </button>
                ) : betweenCycles ? (
                  <div>
                    <button onClick={() => { window.scrollTo(0, 0); setTab('reintro'); setScreen('reintro-tab') }} style={{ background: '#8BAE8A', border: 'none', borderRadius: 'var(--r-md)', padding: '12px 19px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <span style={{ fontSize: 13, color: '#22301F', fontWeight: 600 }}>Pick your next food</span>
                    <span style={{ fontSize: 13, color: '#22301F', fontWeight: 700 }}>{'\u2192'}</span>
                    </button>
                    {!dailyDone && <button onClick={() => setScreen('daily-checkin')} style={{ background: 'transparent', border: 'none', padding: '8px 2px 0', fontSize: 12, color: 'rgba(250,248,244,0.5)', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}>{new Date().getHours() >= 17 ? 'or log tonight\u2019s check-in' : 'or log today\u2019s check-in'}</button>}
                  </div>
                ) : !dailyDone ? (
                  <button onClick={() => setScreen('daily-checkin')} style={{ background: '#8BAE8A', border: 'none', borderRadius: 'var(--r-md)', padding: '12px 19px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <span style={{ fontSize: 13, color: '#22301F', fontWeight: 600 }}>{new Date().getHours() >= 17 ? "Tonight's check-in" : "Today's check-in"}</span>
                    <span style={{ fontSize: 13, color: '#22301F', fontWeight: 700 }}>{'\u2192'}</span>
                  </button>
                ) : (
                  <div style={{ background: 'rgba(139,174,138,0.16)', borderRadius: 'var(--r-md)', padding: '12px 19px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, color: '#8BAE8A', fontWeight: 600 }}>{'\u2713'} Today logged</span>
                  </div>
                )}
              </div>
              <div style={{ position: 'relative' }}>
                <div className="sn-track sn-track-dark">
                  <div className="sn-fill" style={{ width: `${pct}%` }} />
                  <div className="sn-dot sn-dot-dark" style={{ left: `${pct}%` }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
                  <span className="sn-micro sn-micro-dark">{clock.left}</span>
                  <span className="sn-micro sn-micro-dark">{clock.right}</span>
                </div>
              </div>
            </div>
          </div>
          )
        })()}
        <div className="snfy-layout">

          {/* LEFT COLUMN */}
          <div>
            {!cockpitActive && (
            <div className="snfy-greeting sn-rise">
              <h1>{getGreeting()},<br /><em>{name}.</em></h1>
              <p>
                {showIntakeCard ? "Welcome to Sensify. Let's get you set up."
                  : showAuditCard ? "Let's talk about the last few days."
                  : showSlipupCard ? "We logged a slip-up today. Tell us what happened."
                  : showLabCard ? 'Your intake is complete. Upload your lab results to begin.'
                  : showPendingLabCard ? 'Your results are under review.'
                  : needsCommonDecision ? 'Your results are in. Choose your path.'
                  : isTracking ? "You're tracking. Log how today goes."
                  : (profile?.protocol_start_date && currentDay < 1) ? "You're set. Day 1 begins tomorrow."
                  : cleanDays > 0 ? `${cleanDays} clean day${cleanDays !== 1 ? 's' : ''} on the record. Keep going.`
                  : 'Everything starts from here.'}
              </p>
            </div>
            )}

            {/* COMMON TRACK DECISION — shown when assigned common but not yet decided */}
            {needsCommonDecision && (
              <CommonTrackDecision
                session={session}
                profile={profile}
                flaggedCount={labResult?.foods?.length || 0}
                onDecided={loadData}
              />
            )}

            {/* TRACKING LANDING — shown when user declined and is self-tracking */}
            {isTracking && (
              <TrackingLanding
                session={session}
                profile={profile}
                onReverted={loadData}
                onOpenDaily={() => setScreen('daily-checkin')}
                onOpenAsk={() => { window.scrollTo(0, 0); setTab('ask-sensify'); setScreen('ask-sensify') }}
              />
            )}

            {/* DAILY CHECK-IN PROMPT — shown when active and today's check-in isn't done.
                Only once the protocol has actually begun (currentDay >= 1), so it
                doesn't appear the day before, while elimination "starts tomorrow". */}
            {!cockpitActive && !dailyDone && !needsCommonDecision && !showIntakeCard && !showLabCard && !showPendingLabCard &&
             ((currentDay >= 1 && (calculatedPhase === 'elimination' || calculatedPhase === 'reintroduction')) || isTracking) && (
              <button className="snfy-action sn-card-hov sn-rise" style={{ width: '100%', textAlign: 'left', cursor: 'pointer', background: 'linear-gradient(135deg, rgba(139,174,138,0.12), rgba(139,174,138,0.04)), var(--paper)' }} onClick={() => setScreen('daily-checkin')}>
                <div className="sn-micro" style={{ marginBottom: '6px' }}>Daily check-in</div>
                <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--ink)', marginBottom: '3px' }}>How's today going?</div>
                <div style={{ fontSize: '13px', color: '#5A6A55' }}>A few quick taps to log how you slept, your stress, and more. Tap to start →</div>
              </button>
            )}

            {/* MILESTONE MESSAGE */}
            {milestoneMessage && (
              <div className="snfy-action sn-rise" style={{ background: 'linear-gradient(160deg, var(--forest-700) 0%, var(--forest-900) 70%)', boxShadow: 'var(--shadow-3)', color: 'white', position: 'relative' }}>
                <div className="sn-micro sn-micro-dark" style={{ marginBottom: '10px' }}>
                  {milestoneKey === 'day1' ? 'Day 1' : milestoneKey === 'day3' ? 'Day 3' : milestoneKey === 'day14' ? '2 week milestone' : milestoneKey === 'day28' ? 'One month in' : milestoneKey === 'day57' ? 'Reintroduction begins' : 'Milestone'}
                </div>
                {(() => {
                  // Day-1 briefings arrive in three labeled sections; render
                  // them as mono-labeled chunks with YOUR ONE JOB dominant.
                  // Any message without the markers renders as plain prose.
                  const HEADS = ['WHAT CHANGES TODAY:', 'HOW THIS WEEK MAY FEEL:', 'YOUR ONE JOB:']
                  if (HEADS.every(h => String(milestoneMessage).includes(h))) {
                    const txt = String(milestoneMessage)
                    const chunks = HEADS.map((h, i) => {
                      const start = txt.indexOf(h) + h.length
                      const end = i < HEADS.length - 1 ? txt.indexOf(HEADS[i + 1]) : txt.length
                      return { label: h.replace(':', ''), body: txt.slice(start, end).trim() }
                    })
                    return chunks.map((c, i) => (
                      <div key={c.label} style={{ marginBottom: i < 2 ? 14 : 0 }}>
                        <div className="sn-micro" style={{ color: i === 2 ? '#8BAE8A' : 'rgba(250,248,244,0.55)', marginBottom: 4 }}>{c.label}</div>
                        <p style={{ color: i === 2 ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.8)', lineHeight: 1.7, marginBottom: 0, fontSize: i === 2 ? '15px' : '13.5px', fontWeight: i === 2 ? 500 : 400 }}>{c.body}</p>
                      </div>
                    ))
                  }
                  return <p style={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.75, marginBottom: 0, fontSize: '14px' }}>{milestoneMessage}</p>
                })()}
                <button onClick={() => setMilestoneMessage(null)} style={{ position: 'absolute', top: '14px', right: '14px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}>×</button>
              </div>
            )}

            {/* DARK PROTOCOL CARD */}
            {!cockpitActive && (calculatedPhase === 'elimination' || calculatedPhase === 'reintroduction') && profile?.protocol_start_date && (
              <div className="snfy-phase sn-rise-2">
                <div className="snfy-phase-orb" />
                <div className="snfy-phase-orb2" />
                <div className="snfy-phase-eyebrow">{calculatedPhase === 'elimination' ? 'Elimination phase' : 'Reintroduction phase'}</div>
                {currentDay === 0 ? (
                  <>
                    <div style={{ fontFamily: 'Fraunces, serif', fontSize: '34px', fontWeight: 300, color: 'white', lineHeight: 1.15, marginBottom: '6px' }}>Starts <em style={{ fontStyle: 'italic', color: '#8BAE8A' }}>tomorrow.</em></div>
                    <div className="snfy-phase-of">Your elimination phase begins tomorrow.</div>
                    <div className="snfy-pbar"><div className="snfy-pfill" style={{ width: '0%' }}></div></div>
                    <div className="snfy-plabel"><span>Day 1</span><span>56 days total</span></div>
                  </>
                ) : (
                  <>
                    <div className="snfy-phase-day">{currentDay}</div>
                    <div className="snfy-phase-of">of {calculatedPhase === 'elimination' ? '56' : '168'} days</div>
                    <div className="snfy-pbar"><div className="snfy-pfill" style={{ width: `${eliminationProgressPct}%` }}></div></div>
                    <div className="snfy-plabel">
                      <span>Day 1</span>
                      <span>{daysUntilReintro !== null && daysUntilReintro > 0 ? `${daysUntilReintro} days to reintroduction` : calculatedPhase === 'reintroduction' ? 'Reintroduction active' : '56 days total'}</span>
                    </div>
                    <div className="snfy-phase-streak">
                      <div className="snfy-streak-dot"></div>
                      <div className="snfy-streak-text"><span className="snfy-streak-num">{cleanDays} day</span> check-in streak</div>
                    </div>
                    {(() => {
                      // Milestone framing (design item 7): during elimination the
                      // hero names the NEXT earned milestone, not the far wall.
                      // In reintroduction, the full projection returns with the
                      // one-queue math (the retired 113/169 tier floors are gone:
                      // everything unlocks at 57, one 14-day cycle per food).
                      if (!labResult?.foods || !profile?.protocol_start_date) return null
                      const startDate = new Date(profile.protocol_start_date)
                      const dayOf = (dateStr) => Math.floor((new Date(dateStr) - startDate) / 86400000) + 1
                      const todayDay = dayOf(new Date())
                      const freq = profile?.food_frequency || {}
                      const dateLabelFor = (dayN) => {
                        const d = new Date(startDate); d.setDate(d.getDate() + dayN - 1)
                        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      }
                      if (todayDay <= 56) {
                        // First verdict possible: day 56 elimination + one 14-day cycle.
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8BAE8A" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>First verdict possible <span style={{ fontWeight: 500, color: '#8BAE8A' }}>~{dateLabelFor(70)}</span> · Day 70</div>
                          </div>
                        )
                      }
                      const doneOrActive = new Set(reintroRows.map(r => r.food))
                      const active = reintroRows.find(r => r.verdict == null)
                      const remaining = labResult.foods.filter(f => {
                        const q = freq[f.name]
                        return q !== 'never' && !doneOrActive.has(f.name)
                      }).length
                      let cursor = Math.max(57, todayDay)
                      if (active?.started_at) cursor = Math.max(cursor, dayOf(active.started_at) + 14)
                      if (!active && remaining === 0) return null
                      cursor += 14 * remaining
                      const label = dateLabelFor(cursor)
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8BAE8A" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>Projected map completion <span style={{ fontWeight: 500, color: '#8BAE8A' }}>~{label}</span> · Day {cursor}</div>
                        </div>
                      )
                    })()}
                  </>
                )}
              </div>
            )}


            {/* AWAITING APPROVAL state */}
            {profile?.program_phase === 'pending_review' && !showIntakeCard && !showLabCard && (
              <div className="snfy-phase sn-rise">
                <div className="snfy-phase-eyebrow">Your program</div>
                <div style={{ fontFamily: 'Fraunces, serif', fontSize: '22px', fontWeight: 300, color: 'white', marginBottom: '8px' }}>Awaiting <em style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.7)' }}>approval.</em></div>
                {/* Shown precisely when setup IS complete (QA 2026-11-02 #5). */}
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>Setup complete — your results are in review</div>
                <div className="snfy-pbar" style={{ marginTop: '16px' }}><div className="snfy-pfill" style={{ width: '4%' }}></div></div>
                {/* Length is food-count-dependent, and the count is known here
                    (QA 2026-11-03 #6): 8 weeks elimination + ~2 weeks/food. */}
                <div className="snfy-plabel"><span>Setup done</span><span>{(() => {
                  const n = labResult?.foods?.length
                  if (!n) return 'about 6 months'
                  const months = Math.round((56 + n * 14) / 30.4)
                  return `about ${months} month${months === 1 ? '' : 's'} total`
                })()}</span></div>
              </div>
            )}

            {/* ACTION CARDS */}
            {showIntakeCard && (
              <div className="snfy-action">
                <div className="snfy-action-tag"><div className="snfy-action-dot"></div>Complete your setup</div>
                <h2>Start your <em>intake survey.</em></h2>
                <p>Tell us about your symptoms and how often you eat certain foods. 15–20 minutes, and your progress saves as you go.</p>
                <button className="snfy-btn" onClick={() => setScreen('intake')}>Start intake survey →</button>
              </div>
            )}

            {showLabCard && (
              <div className="snfy-action">
                <div className="snfy-action-tag"><div className="snfy-action-dot"></div>Action needed</div>
                <h2>Upload your <em>lab results.</em></h2>
                <p>PDF, photo, or manual entry. The AI reads your results and builds your elimination list in seconds.</p>
                <button className="snfy-btn" onClick={() => setScreen('labresults')}>Upload results →</button>
              </div>
            )}

            {showPendingLabCard && (
              <div className="snfy-pending">
                <div className="snfy-pending-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                </div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '2px' }}>Results under review</div>
                  <div style={{ fontSize: '12px', color: '#7A7A72' }}>Usually reviewed within a day. Check back here — this page updates when it's approved.</div>
                </div>
              </div>
            )}

            {showAuditCard && (
              <div className="snfy-action amber">
                <div className="snfy-action-tag amber">3 days off plan</div>
                <h2>Let's <em className="amber">figure this out together.</em></h2>
                <p>Three days in a row is hard. Let's treat it as information, not failure. Takes about 3 minutes.</p>
                <button className="snfy-btn amber" onClick={() => setScreen('audit')}>Let's talk →</button>
              </div>
            )}

            {pendingAudit && (
              <div className="snfy-audit-pending">
                <div className="snfy-audit-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3D5C3C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                </div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 500, marginBottom: '2px' }}>Guidance being prepared</div>
                  <div style={{ fontSize: '12px', color: '#7A7A72' }}>Usually ready within a day — it will meet you here.</div>
                </div>
              </div>
            )}

            {/* THE GUIDANCE ARRIVES (QA 2026-10-24 #1): the promised response
                used to be written by the review and read by nothing — the
                pending card just vanished. Alex's outcome + note, 7 days. */}
            {!pendingAudit && auditGuidance && (
              <div className="sn-card sn-rise-2" style={{ background: 'linear-gradient(135deg, rgba(139,174,138,0.14), rgba(139,174,138,0.04)), var(--paper)', marginTop: 14 }}>
                <div className="sn-micro" style={{ marginBottom: 8 }}>Your guidance is in</div>
                {auditGuidance.admin_outcome && (
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', marginBottom: 6 }}>{auditGuidance.admin_outcome === 'Reset clock' ? 'Fresh start: your elimination clock has been reset.' : auditGuidance.admin_outcome}</div>
                )}
                <div style={{ fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.65 }}>
                  {auditGuidance.admin_note?.trim() || 'Reviewed personally. Keep logging exactly as you have been — honest days, on-plan or not, are what make your results trustworthy.'}
                </div>
                <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 8, letterSpacing: '1.2px', color: '#9A927E', marginTop: 10 }}>REVIEWED PERSONALLY · {auditGuidance.responded_at ? new Date(auditGuidance.responded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase() : ''}</div>
              </div>
            )}

            {showSlipupCard && (
              <div className="snfy-action amber">
                <div className="snfy-action-tag amber">Slip-up logged today</div>
                <h2>What <em className="amber">happened today?</em></h2>
                <p>Takes 60 seconds. No judgment, just data that makes your weekly insights more accurate.</p>
                <button className="snfy-btn amber" onClick={() => setScreen('slipup')}>Tell us what happened →</button>
              </div>
            )}


            {showReintroCard && (
              <div className="snfy-action">
                <div className="snfy-action-tag">Day 14 · verdict ready</div>
                <h2><em>{profile?.current_reintro_food}</em> reintroduction complete.</h2>
                <p>Complete your survey and get your AI verdict: Safe, Limit, or Avoid.</p>
                <button className="snfy-btn" onClick={() => setScreen('reintro-survey')}>Get my verdict →</button>
              </div>
            )}


            {/* AWAY GATE (finding #7): honest interception after a silent stretch mid-elimination */}
            {awayGap && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,28,28,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}>
                <div style={{ background: '#FAF8F4', borderRadius: 'var(--r-lg)', padding: '26px 24px', maxWidth: 430, width: '100%', boxShadow: '0 30px 80px rgba(0,0,0,0.3)' }}>
                  <div className="sn-micro" style={{ marginBottom: 8 }}>Welcome back · {awayGap.days} days away</div>
                  <div style={{ fontFamily: 'Fraunces, serif', fontSize: 22, fontWeight: 400, color: '#1C1C1C', marginBottom: 8, fontVariationSettings: "'SOFT' 60, 'WONK' 1" }}>{awayGap.hard ? 'Let\u2019s restart your clock.' : 'Were you on your plan?'}</div>
                  <div style={{ fontSize: 13.5, color: '#5A5A52', lineHeight: 1.65, marginBottom: 18 }}>
                    {awayGap.hard
                      ? `It has been ${awayGap.days} days since your last check-in. For your Food Map to mean anything, elimination needs a continuous, verified stretch, so we restart the clock, keeping your lab results and everything else. Day 1 begins today.`
                      : `It has been ${awayGap.days} days since your last check-in. The days themselves are fine. What matters is whether the elimination held while you were away.`}
                  </div>
                  {awayGap.hard ? (
                    <button onClick={async () => {
                      const t = todayLocal()
                      await supabase.from('compliance_audit').insert({ user_id: session.user.id, triggered_at: new Date().toISOString(), trigger_type: 'away_gap_restart', hardest_parts: [], status: 'responded', admin_note: `${awayGap.days} silent days → elimination clock restarted` })
                      await supabase.from('profiles').update({ protocol_start_date: t }).eq('id', session.user.id)
                      window.location.reload()
                    }} style={{ width: '100%', background: '#3D5C3C', color: 'white', border: 'none', borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Restart elimination · day 1 today</button>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <button onClick={async () => {
                        await supabase.from('compliance_audit').insert({ user_id: session.user.id, triggered_at: new Date().toISOString(), trigger_type: 'away_gap_attested', hardest_parts: [], status: 'responded', admin_note: `${awayGap.days} silent days → user attested plan held (gap attested, not observed)` })
                        setAwayGap(null)
                      }} style={{ width: '100%', background: '#3D5C3C', color: 'white', border: 'none', borderRadius: 12, padding: '13px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>Yes, I stayed on my plan</button>
                      <button onClick={async () => {
                        const t = todayLocal()
                        await supabase.from('compliance_audit').insert({ user_id: session.user.id, triggered_at: new Date().toISOString(), trigger_type: 'away_gap_restart', hardest_parts: [], status: 'responded', admin_note: `${awayGap.days} silent days → user chose restart (plan did not hold)` })
                        await supabase.from('profiles').update({ protocol_start_date: t }).eq('id', session.user.id)
                        window.location.reload()
                      }} style={{ width: '100%', background: 'var(--tint-warm)', color: 'var(--ink-soft)', border: 'none', borderRadius: 'var(--r-md)', padding: '13px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>Not really, restart my clock</button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* I TOOK THE TEST — persistent pleasant card until clicked (finding #14) */}
            {!labResult && !normalizeMilestones(profile?.shown_milestones).test_taken && (showIntakeCard || showLabCard || showPendingLabCard) && (
              <div className="sn-card sn-rise-2" style={{ background: 'linear-gradient(135deg, rgba(139,174,138,0.12), rgba(139,174,138,0.04)), var(--paper)', marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div className="sn-micro" style={{ marginBottom: 6 }}>Your lab kit</div>
                  <div style={{ fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.55 }}>Done with your test? Tell us so we know your results are on the way.</div>
                </div>
                <button onClick={async () => { const sm = serializeMilestones({ ...normalizeMilestones(profile?.shown_milestones), test_taken: true }); await supabase.from('profiles').update({ shown_milestones: sm }).eq('id', session.user.id); window.location.reload() }} style={{ background: 'var(--forest-500)', color: 'white', border: 'none', borderRadius: 'var(--r-md)', padding: '11px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: 'var(--shadow-pill)', flexShrink: 0 }}>I took my test</button>
              </div>
            )}

            {/* THE PROTOCOL — designed explainer for the common track (week 1 + eve). Static copy, deliberately not the AI voice.
                Gated on an ACTIVE choice: during awaiting_decision there is no protocol to explain (finding 10). */}
            {profile?.protocol_track === 'common' && profile?.track_decision === 'active' && currentDay <= 7 && (
              <div className="sn-card sn-rise-2" style={{ marginTop: 14 }}>
                <div className="sn-micro sn-micro-dim" style={{ marginBottom: 10 }}>How your protocol works</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                  {[`${labResult?.foods?.length || profile?.track_foods?.length || 8} FOODS REMOVED`, '56 DAYS CLEAN', 'THEN TESTED BACK, ONE AT A TIME'].map(t => (
                    <div key={t} className="sn-micro" style={{ background: 'var(--forest-100)', borderRadius: 'var(--r-pill)', padding: '5px 11px' }}>{t}</div>
                  ))}
                </div>
                <div style={{ fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.65 }}>
                  For the next eight weeks you keep {(labResult?.foods?.length || 8) === 2 ? 'both' : `all ${labResult?.foods?.length || 8}`} foods off your plate while your daily and weekly check-ins track how your body responds. Then each food comes back one at a time, three exposure days and a washout, until every one has a verdict earned from your own data. Nothing gets labeled without being tested.
                </div>
              </div>
            )}

            {/* THE SUSPECT BOARD — what happens next, so it outranks the meters */}
            {labResult && (calculatedPhase === 'elimination' || calculatedPhase === 'reintroduction') && (
              <div className="sn-rise-2" style={{ marginTop: 14 }}><SuspectBoard session={session} profile={profile} labResult={labResult} onOpenReintro={() => goTab('Reintro')} /></div>
            )}

            {/* AI INSIGHT — the voice card */}
            <div className="snfy-insight sn-rise-3" style={{ marginTop: 14 }}>
              <div className="snfy-insight-head">
                <span className="snfy-insight-dot"></span>
                <span className="sn-micro">{
                  profile?.latest_insight ? 'WEEKLY INSIGHT'
                  : (currentDay >= 1 ? 'PROTOCOL' : 'GETTING STARTED')
                }</span>
              </div>
              {profile?.latest_insight ? (
                <div className="snfy-insight-num">Week {profile?.latest_insight_week || 1}</div>
              ) : currentDay >= 1 ? (
                <div className="snfy-insight-num">Day {currentDay}</div>
              ) : null}
              <p>{
                profile?.latest_insight
                || (currentDay >= 1
                  ? `You're on day ${calculatedPhase === 'reintroduction' ? currentDay - 56 : currentDay} of your ${calculatedPhase === 'reintroduction' ? 'reintroduction' : 'elimination'} phase. Your first weekly insight will appear here after your first weekly check-in, drawn from how your symptoms change.`
                  : (profile?.protocol_start_date && currentDay < 1)
                  ? 'You are set. Elimination begins tomorrow. Your day 1 briefing will meet you here.'
                  : 'Once you complete your setup and upload your lab results, your weekly insights will appear here, generated from your symptom data after each check-in.')
              }</p>
              {profile?.latest_insight && <div className="sn-micro sn-micro-dim" style={{ marginTop: 12 }}>GENERATED FROM YOUR CHECK-IN DATA</div>}
            </div>

            {/* WEEKLY CHECK-IN — slim state card */}
            <div className="snfy-weekly-card">
            {showCheckinCard ? (
              <div className={checkinLate ? 'sn-row' : 'sn-row sn-row-up'} style={{ justifyContent: 'space-between', marginTop: 14, background: checkinLate ? 'var(--limit-100)' : undefined }}>
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)' }}>Weekly check-in</div>
                  <div style={{ fontSize: 10.5, color: checkinLate ? 'var(--limit-700)' : 'var(--forest-500)', marginTop: 1 }}>{checkinLate ? "Catch up, your data still matters" : 'Due now · takes 2 minutes'}</div>
                </div>
                <button className={`snfy-btn${checkinLate ? ' amber' : ''}`} style={{ padding: '9px 15px', fontSize: 12.5 }} onClick={() => setScreen('checkin')}>Start →</button>
              </div>
            ) : (
              <div className="sn-row" style={{ justifyContent: 'space-between', marginTop: 14 }}>
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)' }}>Weekly check-in</div>
                  <div style={{ fontSize: 10.5, color: 'var(--gray)', marginTop: 1 }}>{checkins.length === 0 ? 'Opens at the end of week 1' : 'Done. Next one opens at week\u2019s end'}</div>
                </div>
                <span style={{ fontSize: 11, color: 'var(--gray-faint)' }}>{checkins.length === 0 ? 'Locked' : '\u2713'}</span>
              </div>
            )}
            </div>

            {/* BASELINE SHARPNESS — the invisible work of elimination, made visible */}
            {(calculatedPhase === 'elimination' || calculatedPhase === 'reintroduction') && (
              <div className="sn-rise-4" style={{ marginTop: 14 }}><BaselineSharpness session={session} profile={profile} /></div>
            )}

            {/* COMPLIANCE DOTS */}
            {(calculatedPhase === 'elimination' || calculatedPhase === 'reintroduction') && (
              <div className="snfy-comp" style={{ marginTop: '14px' }}>
                {(() => {
                  const now = new Date()
                  const todayStr = localDateString(now)
                  // TRAILING 7 DAYS, ending today. A protocol-week window made
                  // the row contradict the streak the morning after a week
                  // rolled over ("5 day streak" above one filled dot). The
                  // trailing window always shows the streak's own recent days.
                  const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate())
                  const weekStart = new Date(todayLocal)
                  weekStart.setDate(todayLocal.getDate() - 6)
                  let startDayNum = null
                  if (profile?.protocol_start_date) {
                    const [yy, mm, dd] = profile.protocol_start_date.split('T')[0].split('-').map(Number)
                    const protoStart = new Date(yy, mm - 1, dd)
                    startDayNum = Math.round((weekStart - protoStart) / (1000 * 60 * 60 * 24)) + 1
                  }
                  const dowLabels = ['Su','Mo','Tu','We','Th','Fr','Sa']
                  const days = Array.from({ length: 7 }).map((_, i) => {
                    const td = new Date(weekStart)
                    td.setDate(weekStart.getDate() + i)
                    const dateStr = localDateString(td)
                    const entry = complianceData.find(c => c.date === dateStr)
                    const isFuture = dateStr > todayStr
                    const isToday = dateStr === todayStr
                    const day = dowLabels[td.getDay()]
                    let cls, mark
                    if (entry?.response === 'YES') { cls = 'yes'; mark = '\u2713' }        // did check-in + complied
                    else if (entry?.response === 'NO') { cls = 'no'; mark = '\u2717' }     // did check-in + slipped
                    else if (isFuture) { cls = 'future'; mark = '' }
                    else if (profile?.protocol_start_date && dateStr < String(profile.protocol_start_date).split('T')[0]) { cls = 'future'; mark = '' }  // pre-protocol days: blank, never 'missed' (finding #8)
                    else if (isToday) { cls = 'empty'; mark = '\u00b7' }                   // today, pending - not missed yet
                    else if (awayDates.has(dateStr)) { cls = 'away'; mark = '\u25cb' }     // away, on plan (user's word): pauses, never resets
                    else { cls = 'missed'; mark = '\u2013' }  // forgot — the only streak-breaker
                    return { day, cls: cls + (isToday ? ' today' : ''), mark, dateStr, isFuture }
                  })
                  // Early in the protocol the trailing window reaches before day 1;
                  // pre-protocol dots already render blank, so clamp the label.
                  const weekLabel = startDayNum !== null
                    ? (startDayNum >= 1 ? `Days ${startDayNum}\u2013${startDayNum + 6}`
                      : startDayNum + 6 <= 1 ? 'Day 1'   // day one: never "Days 1\u20131" (QA 2026-10-23 #5)
                      : `Days 1\u2013${startDayNum + 6}`)
                    : 'Last 7 days'
                  return (
                    <>
                      <div className="snfy-comp-top">
                        <div className="sn-micro">{weekLabel}</div>
                        {/* The streak number lives in the hero ring only (QA
                            2026-10-23 #5): this card keeps the dots + window. */}
                      </div>
                      <div className="snfy-comp-dots">
                        {days.map((d, i) => (
                          <div key={i} className={`snfy-dot ${d.cls}`}>
                            <span className="snfy-dot-day">{d.day}</span>
                            <span className="snfy-dot-mark">{d.mark}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '10px', justifyContent: 'center', alignItems: 'center' }}>
                        {[
                          { bg: '#4A8C6A', fg: 'white', m: '\u2713', label: 'On plan' },
                          { bg: '#D64545', fg: 'white', m: '\u2717', label: 'Slipped' },
                          { bg: '#DDDAD1', fg: '#6A6A62', m: '\u2013', label: 'No check-in' },
                        ].map((l, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ width: '14px', height: '14px', borderRadius: '4px', background: l.bg, color: l.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', fontWeight: 700, flexShrink: 0 }}>{l.m}</span>
                            <span style={{ fontSize: '10px', color: '#8A8A82' }}>{l.label}</span>
                          </div>
                        ))}
                        <button onClick={() => setLegendOpen(v => !v)} style={{ background: 'transparent', border: 'none', padding: 0, fontSize: '10px', color: '#3D5C3C', textDecoration: 'underline', cursor: 'pointer' }}>{legendOpen ? 'Hide' : 'What do these mean?'}</button>
                      </div>
                      {legendOpen && (
                        <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {[
                            { bg: '#4A8C6A', fg: 'white', m: '\u2713', title: 'On plan', text: 'You checked in and stayed on your plan. A clean day. Builds your streak.' },
                            { bg: '#D64545', fg: 'white', m: '\u2717', title: 'Slipped', text: 'You checked in and honestly logged eating something off-plan. Your streak keeps going. An honest slip is real data, and it never counts against you.' },
                            { bg: '#DDDAD1', fg: '#6A6A62', m: '\u2013', title: 'No check-in', text: 'The day went unlogged with no explanation, so there is no data and no word. This is the one that resets your streak. A slip we can work with. A blank we cannot.' },
                            { bg: 'transparent', bd: '1.5px solid #8BAE8A', fg: '#3D5C3C', m: '\u25cb', title: 'Away, on plan', text: 'You were away for a stretch, and when you returned you told us you stayed on your plan. We take your word for the days, so your streak pauses instead of resetting. But only a check-in creates data, so these days stay out of your analysis.' },
                          ].map((l, i) => (
                            <div key={i} style={{ display: 'flex', gap: '10px', background: 'var(--tint-warm)', borderRadius: 'var(--r-sm)', padding: '10px 12px' }}>
                              <span style={{ width: '18px', height: '18px', borderRadius: '5px', background: l.bg, color: l.fg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, flexShrink: 0, marginTop: '1px' , border: l.bd || 'none' }}>{l.m}</span>
                              <div>
                                <div style={{ fontSize: '12px', fontWeight: 600, color: '#1C1C1C', marginBottom: '2px' }}>{l.title}</div>
                                <div style={{ fontSize: '11.5px', color: '#7A7A72', lineHeight: 1.55 }}>{l.text}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <WeekFactorCards days={days} factors={weekFactors} profile={profile} />
                    </>
                  )
                })()}
              </div>
            )}
          </div>

          {/* RIGHT COLUMN */}
          <div>
            {/* FIRST WEEK GUIDE (#19, button + panel form) — days 1-7, retires after first weekly check-in */}
            {calculatedPhase === 'elimination' && currentDay >= 1 && currentDay <= 7 && checkins.length === 0 && (() => {
              const flagged = (labResult?.foods || []).filter(f => f.level !== 'No sensitivity')
              const goto = (t, s) => { setGuideOpen(false); window.scrollTo(0, 0); setTab(t); setScreen(s) }
              return (
                <>
                  <button className="snfy-guide-slot sn-card sn-card-hov sn-rise" onClick={() => setGuideOpen(true)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, border: 'none', padding: '14px 15px', cursor: 'pointer', textAlign: 'left', marginBottom: 14 }}>
                    <div style={{ width: 34, height: 34, borderRadius: 10, background: '#3D5C3C', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1C1C1C' }}>Your first-week guide</div>
                      <div style={{ fontSize: 11.5, color: '#7A7A72', marginTop: 1 }}>What to do, and what every tab is for</div>
                    </div>
                    <span style={{ color: '#3D5C3C', fontSize: 16, fontWeight: 600 }}>›</span>
                  </button>

                  {guideOpen && (
                    <div onClick={() => setGuideOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(28,28,28,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}>
                      <div onClick={e => e.stopPropagation()} style={{ background: '#FAF8F4', borderRadius: 'var(--r-lg)', padding: '20px 18px', maxWidth: 420, width: '100%', maxHeight: '86vh', overflowY: 'auto', boxShadow: '0 30px 80px rgba(0,0,0,0.25)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                          <div>
                            <div className="sn-micro" style={{ marginBottom: 6 }}>Week one · Day {currentDay} of 7</div>
                            <div style={{ fontFamily: 'Fraunces, serif', fontSize: 23, fontWeight: 400, color: '#1C1C1C', fontVariationSettings: "'SOFT' 60, 'WONK' 1" }}>Your first week.</div>
                          </div>
                          <button onClick={() => setGuideOpen(false)} style={{ width: 27, height: 27, borderRadius: '50%', background: '#EFEDE6', border: 'none', color: '#7A7A72', fontSize: 13, cursor: 'pointer', flexShrink: 0 }}>✕</button>
                        </div>
                        <div style={{ fontSize: 12.5, color: '#7A7A72', lineHeight: 1.65, marginBottom: 18 }}>{currentDay === 1 ? 'Elimination starts today.' : 'Elimination is underway.'} Eight clean weeks gives your body a quiet baseline, and every answer you earn later is measured against it.</div>

                        <div style={{ display: 'flex', gap: 12, marginBottom: 15 }}>
                          <div style={{ width: 25, height: 25, borderRadius: '50%', background: '#3D5C3C', color: '#fff', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>1</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13.5, fontWeight: 600, color: '#1C1C1C', marginBottom: 6 }}>{profile?.protocol_track === 'common' ? 'Stop eating your trigger foods' : 'Stop eating your flagged foods'}</div>
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 6 }}>
                              {flagged.map(f => <span key={f.name} style={{ background: '#EDF3ED', color: '#3D5C3C', borderRadius: 9, padding: '4px 11px', fontSize: 11.5, fontWeight: 600 }}>{f.name}</span>)}
                            </div>
                            <button onClick={() => goto('verdicts', 'food-map')} style={{ background: 'none', border: 'none', padding: 0, fontSize: 12, color: '#3D5C3C', fontWeight: 600, cursor: 'pointer' }}>{profile?.protocol_track === 'common' ? 'See your foods \u2192' : 'See sensitivity levels \u2192'}</button>
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: 12, marginBottom: 15 }}>
                          <div style={{ width: 25, height: 25, borderRadius: '50%', background: dailyDone ? '#3D5C3C' : '#8BAE8A', color: '#fff', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {dailyDone ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg> : '2'}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13.5, fontWeight: 600, color: '#1C1C1C', marginBottom: 2 }}>{dailyDone ? (currentDay === 1 ? 'First check-in logged' : "Today's check-in logged") : 'Check in every day'}</div>
                            <div style={{ fontSize: 12, color: '#7A7A72', lineHeight: 1.6 }}>{dailyDone ? `Day ${currentDay === 1 ? 'one' : currentDay} is on the record. ${7 - currentDay} more night${7 - currentDay !== 1 ? 's' : ''} this week, ten seconds each.` : `Ten seconds each night: did you stick to it, how you slept, how you feel. ${currentDay === 1 ? 'Tonight is your first.' : 'Tonight counts.'}`}</div>
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: 12, marginBottom: 17 }}>
                          <div style={{ width: 25, height: 25, borderRadius: '50%', background: '#E0DED6', color: '#7A7A72', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>3</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13.5, fontWeight: 600, color: '#1C1C1C', marginBottom: 2 }}>Once a week, rate the week</div>
                            <div style={{ fontSize: 12, color: '#7A7A72', lineHeight: 1.6 }}>A short survey scoring the week as a whole. It builds the trend line your progress is measured on. Your first lands {currentDay >= 7 ? 'today' : currentDay === 6 ? 'tomorrow' : `in ${7 - currentDay} days`}.</div>
                          </div>
                        </div>

                        <div style={{ borderTop: '1px solid rgba(0,0,0,0.07)', paddingTop: 15 }}>
                          <div className="sn-micro sn-micro-dim" style={{ marginBottom: 4 }}>Know your tabs</div>
                          <div style={{ fontSize: 11.5, color: '#A8A69E', marginBottom: 12 }}>Tap any of them to look around. Nothing breaks.</div>
                          {[
                            ['Ask Sensify', 'Can I eat this? What can I eat at an Italian restaurant? Ask anything, starting now.', 'ask-sensify', 'ask-sensify'],
                            ['History', 'Every check-in logged, and your symptoms graphed against your baseline so you can see yourself getting better.', 'history', 'checkin-history'],
                            ['Reintro', `From day 57, you eat ${profile?.protocol_track === 'common' ? 'each food' : 'your flagged foods'} again one at a time to find out which ones actually cause problems.`, 'reintro', 'reintro-tab'],
                            ['Verdicts', `Where it all ends up. ${profile?.protocol_track === 'common' ? 'The foods you are testing' : 'Your flagged foods'} today, turning into answers you've earned along the way.`, 'verdicts', 'food-map'],
                          ].map(([nm, desc, t, scr], i, arr) => (
                            <button key={nm} onClick={() => goto(t, scr)} style={{ display: 'flex', gap: 11, alignItems: 'center', padding: '11px 13px', borderRadius: 'var(--r-md)', background: '#FFFFFF', border: 'none', boxShadow: 'var(--shadow-1)', marginBottom: i < arr.length - 1 ? 7 : 0, width: '100%', cursor: 'pointer', textAlign: 'left', transition: 'box-shadow 0.15s, transform 0.15s' }} onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 16px rgba(34,48,31,0.1)'; e.currentTarget.style.transform = 'translateY(-1px)' }} onMouseLeave={e => { e.currentTarget.style.boxShadow = 'var(--shadow-1)'; e.currentTarget.style.transform = 'none' }}>
                              <span style={{ background: '#EDF3ED', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 600, color: '#3D5C3C', flexShrink: 0 }}>{nm}</span>
                              <span style={{ fontSize: 11.5, color: '#7A7A72', lineHeight: 1.5, flex: 1 }}>{desc}</span>
                              <span style={{ color: '#3D5C3C', fontSize: 14, fontWeight: 600 }}>›</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )
            })()}
            {/* FULL CATEGORIZED AVOIDING LIST */}
            <div className="snfy-avoiding sn-rise-2">
              <div className="snfy-avoid-header">
                <div className="sn-micro" style={{ marginBottom: 0 }}>Currently avoiding</div>
                {showAvoidingList && <div className="sn-micro sn-micro-dim">{avoidedFoods.length} foods total</div>}
              </div>

              {showAvoidingList && avoidedFoods.length > 0 ? (
                <>
                  {(() => {
                    // Severity stays semantic but quiet: saturated color lives only in the
                    // small group dots; the bar uses the 100-tints and chips are neutral,
                    // so earned verdict stamps keep the loudest color on the panel.
                    const colors = { High: '#D64545', Moderate: '#E8941F', Low: '#2C9D8A', Common: '#8BAE8A' }
                    const barTints = { High: 'var(--avoid-100)', Moderate: 'var(--limit-100)', Low: 'var(--safe-100)', Common: 'var(--forest-100)' }
                    const order = ['High', 'Moderate', 'Low', 'Common']
                    const grouped = order.map(level => ({
                      level,
                      foods: avoidedFoods.filter(f => f.level === level),
                    })).filter(g => g.foods.length > 0)

                    return (
                      <>
                        {/* Proportion bar — whole sensitivity profile at a glance */}
                        <div className="snfy-avoid-bar">
                          {grouped.map(g => (
                            <div key={g.level} className="snfy-avoid-bar-seg" style={{ flex: g.foods.length, background: barTints[g.level] }} />
                          ))}
                        </div>

                        {grouped.map(g => (
                          <div key={g.level} className="snfy-avoid-group">
                            <div className="snfy-avoid-group-head">
                              <div className="snfy-avoid-level-dot" style={{ background: colors[g.level] }}></div>
                              <div className="sn-micro sn-micro-dim">{g.level === 'Common' ? 'Common triggers \u00b7 not lab-flagged' : `${g.level} sensitivity`}</div>
                              <div className="sn-micro sn-micro-dim" style={{ marginLeft: 'auto' }}>{g.foods.length}</div>
                            </div>
                            <div className="snfy-avoid-chips">
                              {g.foods.map((food, i) => {
                                const v = latestCycleByFood[(food.name || '').toLowerCase()]?.verdict
                                if (v === 'Avoid' || v === 'Limit') {
                                  return (
                                    <span key={i} className="snfy-avoid-chip" style={{ background: 'var(--tint-warm)', color: 'var(--ink)' }}>
                                      {food.name}
                                      <span className={`sn-stamp ${v === 'Avoid' ? 'sn-stamp-avoid' : 'sn-stamp-limit'}`}>{v}</span>
                                    </span>
                                  )
                                }
                                return <span key={i} className="snfy-avoid-chip" style={{ background: 'var(--tint-warm)', color: 'var(--ink)' }}>{food.name}</span>
                              })}
                            </div>
                          </div>
                        ))}
                      </>
                    )
                  })()}
                </>
              ) : (
                <div style={{ padding: '20px 0', textAlign: 'center' }}>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: '#1C1C1C', marginBottom: '6px' }}>No foods yet</div>
                  <div style={{ fontSize: '12px', color: '#7A7A72', lineHeight: 1.65 }}>
                    {!labResult ? 'Your elimination list will appear here once your lab results are uploaded and approved.'
                      : labResult.status === 'pending_review' ? 'Your lab results are under review.'
                      : 'No foods flagged on your elimination list.'}
                  </div>
                </div>
              )}

            </div>

            {isTestAccount(session?.user?.email) && (
              <div style={{ position: 'fixed', bottom: window.innerWidth <= 640 ? 86 : 14, right: 14, zIndex: 70, background: '#FFFFFF', border: '1.5px dashed #C07A28', borderRadius: 12, padding: '10px 12px', boxShadow: '0 6px 20px rgba(0,0,0,0.12)', fontFamily: 'DM Mono, monospace' }}>
                <div style={{ fontSize: 8, letterSpacing: '1.4px', color: '#C07A28', marginBottom: 7 }}>TEST MODE · TIME MACHINE</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[1, 7, 11, 43].map(d => (
                    <button key={d} onClick={async (ev) => { ev.currentTarget.disabled = true; await testSkipDays(supabase, session.user.id, d); window.location.reload() }} style={{ fontFamily: 'DM Mono, monospace', fontSize: 10, padding: '6px 10px', borderRadius: 8, border: '1px solid rgba(192,122,40,0.4)', background: '#FBF0DF', color: '#9A6212', cursor: 'pointer' }}>+{d}d</button>
                  ))}
                </div>
              </div>
            )}
            {/* HOUSEHOLD CARD — utility row, column footer per the design direction */}
            {(calculatedPhase === 'elimination' || calculatedPhase === 'reintroduction') && (
              <div className="sn-rise-3" style={{ marginTop: 14 }}><HouseholdShareCard session={session} profile={profile} /></div>
            )}

            {/* THE LEAF CONSTELLATION — 28 nights, one star per logged day, randomized per user; the leaf reveals at 28 */}
            {/* Lives through all of elimination, not just day 42 (QA 2026-11-03
                #4): a slow logger past day 42 with <28 stars used to lose the
                card — and its "pattern detection unlocks" promise — mid-story.
                Day 57 hands off to the reintro hero naturally. */}
            {((calculatedPhase === 'elimination' && currentDay >= 1 && currentDay <= 56) || (!profile?.protocol_start_date && (showLabCard || showPendingLabCard))) && (() => {
              const preP = !profile?.protocol_start_date
              const pStartIso = preP ? null : String(profile.protocol_start_date).split('T')[0]
              const todayIso = localDateString(new Date())
              const awayN = preP ? 0 : [...awayDates].filter(d => d >= pStartIso && d <= todayIso).length
              // Away-on-plan nights hang stars too (Alex's ruling): your word counts in the sky.
              // They render as hollow rings — same ○ language as the week calendar — so the
              // record stays legible: filled = logged, ring = on your word.
              // Only LOGGED nights advance the 28 (QA 2026-10-23 #4): pattern
              // detection needs real data, and the compliance legend already
              // says away days stay out of the analysis. Away nights still get
              // a mention in the copy, but hang no counted star.
              const starCount = preP ? 0 : Math.max(0, Math.min(recordStats.days, currentDay, 28))
              const complete = starCount >= 28
              // 28 fixed anchors of the leaf (x, y, r)
              const A = [[140,186,2],[140,177,1.5],[140,168,2.2],[106,146,1.7],[174,146,1.7],[86,116,1.7],[194,116,1.7],[81,86,1.7],[199,86,1.7],[92,52,1.7],[188,52,1.7],[112,26,1.7],[168,26,1.7],[126,14,1.5],[154,14,1.5],[140,8,2.2],[140,140,1.6],[140,108,1.6],[140,76,1.6],[100,106,1.4],[180,106,1.4],[106,74,1.4],[174,74,1.4],[118,42,1.4],[162,42,1.4],[122,122,1.3],[158,122,1.3],[140,58,1.3]]
              const ADJ = [[0,1],[1,2],[2,3],[3,5],[5,7],[7,9],[9,11],[11,13],[13,15],[15,14],[14,12],[12,10],[10,8],[8,6],[6,4],[4,2],[2,16],[16,17],[17,18],[18,27],[27,15],[16,19],[16,20],[17,21],[17,22],[18,23],[18,24],[25,16],[26,16]]
              // Deterministic per-user rng: fill order + tiny jitter, stable across reloads, unique per user
              let seed = 0
              const uid = session?.user?.id || 'sensify'
              for (let i = 0; i < uid.length; i++) seed = (seed * 31 + uid.charCodeAt(i)) >>> 0
              const rng = () => { seed = (seed + 0x6D2B79F5) >>> 0; let t = seed; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296 }
              const order = A.map((_, i) => i)
              for (let i = order.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); const tmp = order[i]; order[i] = order[j]; order[j] = tmp }
              const jit = A.map(() => [(rng() - 0.5) * 4, (rng() - 0.5) * 4])
              const present = new Set(order.slice(0, starCount))
              const P = A.map(([x, y, r], i) => [x + jit[i][0], y + jit[i][1], r])
              return (
                <div className="snfy-garden sn-card sn-rise-4" style={{ marginTop: 14 }}>
                  <style>{`
                    @keyframes lcTw { 0%,100% { opacity: 1 } 50% { opacity: 0.35 } }
                    @keyframes lcGp { 0%,100% { opacity: 1 } 50% { opacity: 0.7 } }
                  `}</style>
                  <div style={{ fontFamily: 'Fraunces, serif', fontSize: 19, fontWeight: 400, color: '#1C1C1C', lineHeight: 1.25, marginBottom: 7, fontVariationSettings: "'SOFT' 60, 'WONK' 1" }}>
                    {preP ? <>Your sky is <span style={{ color: '#3D5C3C' }}>waiting.</span></> : complete ? <>Your pattern <span style={{ color: '#3D5C3C' }}>took shape.</span></> : starCount < 8 ? <>Every check-in <span style={{ color: '#3D5C3C' }}>hangs a star.</span></> : <>Sensify is <span style={{ color: '#3D5C3C' }}>connecting your days.</span></>}
                  </div>
                  <div style={{ fontSize: 12, color: '#5A5A52', lineHeight: 1.6, marginBottom: 10 }}>
                    {preP ? <>Every day you log becomes a star. At 28 nights they form a constellation, and pattern detection unlocks. Your first check-in hangs the first star.</> : complete ? <>Your constellation is complete. <b style={{ color: '#1C1C1C', fontWeight: 600 }}>Pattern detection is live</b>: sleep, stress, and symptoms, cross-referenced from here on.</> : <><b style={{ color: '#1C1C1C', fontWeight: 600 }}>{starCount}</b> night{starCount === 1 ? '' : 's'} on the record{awayN > 0 ? <> ({awayN} away night{awayN === 1 ? '' : 's'} on your word, held out of the count)</> : ''}{recordStats.events > 0 ? <>, <b style={{ color: '#1C1C1C', fontWeight: 600 }}>{recordStats.events}</b> symptom event{recordStats.events === 1 ? '' : 's'}</> : ''}. At 28, your stars form a <b style={{ color: '#1C1C1C', fontWeight: 600 }}>constellation</b>, and <b style={{ color: '#1C1C1C', fontWeight: 600 }}>pattern detection unlocks</b>: sleep, stress, and symptoms, cross-referenced for connections.</>}
                  </div>
                  <svg width="100%" viewBox="0 0 280 195" style={{ display: 'block', borderRadius: 14 }}>
                    <defs>
                      <radialGradient id="lcSky" cx="50%" cy="42%" r="82%"><stop offset="0%" stopColor="#1B2B27" /><stop offset="100%" stopColor="#101B18" /></radialGradient>
                      <filter id="lcGlow" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur stdDeviation="1.5" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
                    </defs>
                    <rect width="280" height="195" fill="url(#lcSky)" />
                    {complete ? (
                      <g style={{ animation: 'lcGp 4.5s ease-in-out infinite' }}>
                        <g stroke="#8BAE8A" fill="none">
                          <path d="M140 168 C110 152 88 128 82 100 C78 74 88 48 104 32 C116 20 128 12 140 8 C152 12 164 20 176 32 C192 48 202 74 198 100 C192 128 170 152 140 168" strokeWidth="1" opacity="0.6" />
                          <path d="M140 186 L140 168 C140 140 139 110 140 76 C140 50 140 26 140 8" strokeWidth="0.9" opacity="0.55" />
                          <path d="M140 140 C122 132 108 120 100 106" strokeWidth="0.65" opacity="0.42" />
                          <path d="M140 108 C124 100 112 88 106 74" strokeWidth="0.65" opacity="0.42" />
                          <path d="M140 76 C128 66 120 54 118 42" strokeWidth="0.65" opacity="0.42" />
                          <path d="M140 140 C158 132 172 120 180 106" strokeWidth="0.65" opacity="0.42" />
                          <path d="M140 108 C156 100 168 88 174 74" strokeWidth="0.65" opacity="0.42" />
                          <path d="M140 76 C152 66 160 54 162 42" strokeWidth="0.65" opacity="0.42" />
                        </g>
                        {/* All 28 counted stars are logged nights now (Deploy 11
                            retired away-ring markers); the second ringSet here
                            survived that pass and crashed Home for every
                            complete-constellation user (QA 2026-10-29). */}
                        <g filter="url(#lcGlow)">
                          {P.map(([x, y, r], i) => <circle key={i} cx={x} cy={y} r={r} fill="#8BAE8A" />)}
                        </g>
                      </g>
                    ) : (
                      <>
                        {starCount >= 8 && (
                          <g stroke="#8BAE8A" fill="none" opacity="0.5">
                            {ADJ.filter(([a, b]) => present.has(a) && present.has(b)).map(([a, b], i) => (
                              <line key={i} x1={P[a][0]} y1={P[a][1]} x2={P[b][0]} y2={P[b][1]} strokeWidth="0.7" />
                            ))}
                          </g>
                        )}
                        <g filter="url(#lcGlow)">
                          {P.map(([x, y, r], i) => present.has(i)
                            ? <circle key={i} cx={x} cy={y} r={r} fill="#FAF8F4" style={{ animation: `lcTw 3.4s ease-in-out ${(i % 3) * 1.1}s infinite` }} />
                            : null)}
                        </g>
                      </>
                    )}
                  </svg>
                  <div className={complete ? 'sn-micro' : 'sn-micro sn-micro-dim'} style={{ marginTop: 9 }}>{preP ? 'OPENS WITH DAY 1' : complete ? 'NIGHT 28 · CONSTELLATION COMPLETE' : `NIGHT ${starCount} OF 28 · ${28 - starCount} STAR${28 - starCount === 1 ? '' : 'S'} TO GO`}</div>
                </div>
              )
            })()}

            {/* SYMPTOM TRENDS — ambient monitor */}
            <div className="snfy-trends">
              <SymptomGraph profile={profile} checkins={checkins} activeMetric={activeGraphMetric} setActiveMetric={setActiveGraphMetric} />
            </div>

          </div>

        </div>
      </>)}

      {/* CONFETTI OVERLAY */}
      {showConfetti && <ConfettiOverlay />}
    </div>
  )
}
